import React, { useState, useEffect, useRef } from 'react';
import { 
  Dumbbell, TrendingUp, Plus, Trash2, Clock, ChevronDown, ChevronUp, Activity, 
  Zap, Settings, Filter, CheckCircle2, XCircle, History, Globe, Wifi, WifiOff, 
  Quote, Search, Dna, BrainCircuit, Scale, Percent, User, List, Image as ImageIcon, 
  Edit3, Save, X, CalendarDays, Play, SkipForward, RotateCcw, Info, Youtube, 
  BookOpen, Timer, FastForward, Trophy, Upload, Cloud, Loader2, Medal, Target, 
  Camera, Calendar, Ruler, Calculator, BarChart3, Disc, Hash, Flame, Ghost, AlertTriangle,
  Download, Pill, Bell, ChevronRight, MoreHorizontal, BatteryCharging
} from 'lucide-react';

// Firebase Imports
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken, 
  onAuthStateChanged, 
  User as FirebaseUser 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  onSnapshot, 
  deleteDoc
} from 'firebase/firestore';

// ==========================================
// 🚀 DEINE LIVE-CONFIG
// ==========================================
const YOUR_FIREBASE_CONFIG = {
  apiKey: "AIzaSyDwcCLgRwQ694x8KDH4dFY6CTpxxZLSMIc",
  authDomain: "fittrack-live-d0626.firebaseapp.com",
  projectId: "fittrack-live-d0626",
  storageBucket: "fittrack-live-d0626.firebasestorage.app",
  appId: "1:830522196762:web:1a907684a0cf66cafaa2a3",
  messagingSenderId: "830522196762"
};

const isConfigured = YOUR_FIREBASE_CONFIG.apiKey !== "dummy";
const firebaseConfig = isConfigured 
  ? YOUR_FIREBASE_CONFIG 
  : JSON.parse(typeof __firebase_config !== 'undefined' ? __firebase_config : '{}');

// GLOBALE INITIALISIERUNG
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = typeof __app_id !== 'undefined' ? __app_id : 'default-app-id';

// Helper für saubere Pfade
const getCollectionRef = (userId: string, collectionName: string) => {
  if (isConfigured) {
    return collection(db, 'users', userId, collectionName);
  } else {
    return collection(db, 'artifacts', appId, 'users', userId, collectionName);
  }
};

const getDocRef = (userId: string, ...pathSegments: string[]) => {
  if (isConfigured) {
     // @ts-ignore
    return doc(db, 'users', userId, ...pathSegments);
  } else {
     // @ts-ignore
    return doc(db, 'artifacts', appId, 'users', userId, ...pathSegments);
  }
};

const getUserCollection = (userId: string, collectionName: string) => {
  return getCollectionRef(userId, collectionName);
};

// --- Types & Interfaces ---

type EquipmentType = 
  | 'bodyweight' | 'dumbbell' | 'barbell' | 'machine' | 'cable' | 'kettlebell' 
  | 'hometrainer' | 'crosstrainer' | 'multigym' | 'bench' | 'incline_bench';

type FitnessLevel = 'Beginner' | 'Intermediate' | 'Expert';

interface Set {
  id: string;
  reps: number;
  weight: number;
  completed: boolean;
}

interface Exercise {
  id: string;
  name: string;
  muscleGroup: string;
  equipment: EquipmentType;
  sets: Set[];
  imageUrl?: string | null; 
  instructions?: string | null;
  videoUrl?: string | null;
  secondaryMuscles?: string[] | null;
}

interface WorkoutSession {
  id: string;
  date: string; 
  name: string;
  duration: number;
  exercises: Exercise[];
  totalVolume: number;
}

interface PlannedWorkout {
  id: string;
  date: string; 
  dayName: string; 
  workoutTemplate: WorkoutSession | null; 
  completed: boolean;
  type: 'Strength' | 'Cardio' | 'Rest';
}

interface ExerciseTemplate {
  id: string;
  name: string;
  muscleGroup: string;
  equipment: EquipmentType;
  imageUrl?: string | null;
  instructions?: string | null;
  secondaryMuscles?: string[] | null;
  videoUrl?: string | null;
  isCustom?: boolean; 
}

interface QuoteData {
  text: string;
  author: string;
}

interface BodyStat {
  id: string;
  date: string;
  weight: number; 
  bodyFat?: number | null; 
  muscleMass?: number | null; 
}

interface Supplement {
  id: string;
  name: string;
  dosage: string;
  time: string; // HH:MM
  lastTaken: string | null; // ISO Date String
}

// --- Mock Data / Initial State ---

const MUSCLE_IMAGES: Record<string, string> = {
  BRUST: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=300&q=80",
  RÜCKEN: "https://images.unsplash.com/photo-1605296860738-75d05f70e3dd?auto=format&fit=crop&w=300&q=80",
  BEINE: "https://images.unsplash.com/photo-1434608519381-a1f6091a70c8?auto=format&fit=crop&w=300&q=80",
  SCHULTERN: "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?auto=format&fit=crop&w=300&q=80",
  ARME: "https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?auto=format&fit=crop&w=300&q=80",
  RUMPF: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=300&q=80",
  AUSDAUER: "https://images.unsplash.com/photo-1538805060528-035496c41e41?auto=format&fit=crop&w=300&q=80"
};

const EQUIPMENT_LIST: {id: EquipmentType, label: string, category: string}[] = [
  { id: 'bodyweight', label: 'Körpergewicht', category: 'Basis' },
  { id: 'dumbbell', label: 'Kurzhanteln', category: 'Freihanteln' },
  { id: 'barbell', label: 'Langhantel', category: 'Freihanteln' },
  { id: 'kettlebell', label: 'Kettlebell', category: 'Freihanteln' },
  { id: 'bench', label: 'Hantelbank', category: 'Bänke' },
  { id: 'incline_bench', label: 'Schrägbank', category: 'Bänke' },
  { id: 'multigym', label: 'Kettler Fitmaster', category: 'Maschinen' },
  { id: 'machine', label: 'Andere Maschinen', category: 'Maschinen' },
  { id: 'cable', label: 'Kabelzug', category: 'Maschinen' },
  { id: 'hometrainer', label: 'Hometrainer', category: 'Cardio' },
  { id: 'crosstrainer', label: 'Crosstrainer', category: 'Cardio' },
];

const INITIAL_EXERCISES: ExerciseTemplate[] = [
  // --- BRUST ---
  { 
    id: 'ch1', 
    name: 'Bankdrücken (Langhantel)', 
    muscleGroup: 'BRUST', 
    equipment: 'barbell', 
    imageUrl: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=300&q=80",
    instructions: "Lege dich flach auf die Bank. Greife die Stange etwas weiter als schulterbreit. Senke die Stange kontrolliert bis zur mittleren Brust ab und drücke sie explosiv nach oben. Atme beim Drücken aus.",
    secondaryMuscles: ["Trizeps", "Vordere Schulter"],
    videoUrl: "https://www.youtube.com/results?search_query=bankdr%C3%BCcken+langhantel+technik"
  },
  { 
    id: 'ch2', 
    name: 'Bankdrücken (Kurzhantel)', 
    muscleGroup: 'BRUST', 
    equipment: 'dumbbell', 
    secondaryMuscles: ["Trizeps", "Vordere Schulter"],
    instructions: "Lege dich mit zwei Kurzhanteln auf die Flachbank. Drücke die Hanteln von der Schulterhöhe gerade nach oben zusammen, ohne dass sie sich berühren. Senke sie langsam wieder ab.",
    videoUrl: "https://www.youtube.com/results?search_query=kurzhantel+bankdr%C3%BCcken"
  },
  { id: 'ch3', name: 'Schrägbankdrücken (LH)', muscleGroup: 'BRUST', equipment: 'incline_bench', secondaryMuscles: ["Trizeps", "Vordere Schulter"], instructions: "Wie Bankdrücken, aber auf einer um 30-45 Grad geneigten Bank. Betont die obere Brust.", videoUrl: "https://www.youtube.com/results?search_query=schr%C3%A4gbankdr%C3%BCcken+langhantel" },
  { id: 'ch4', name: 'Schrägbankdrücken (KH)', muscleGroup: 'BRUST', equipment: 'incline_bench', secondaryMuscles: ["Trizeps", "Vordere Schulter"], instructions: "Wie KH-Bankdrücken auf geneigter Bank. Achte auf stabile Handgelenke.", videoUrl: "https://www.youtube.com/results?search_query=schr%C3%A4gbankdr%C3%BCcken+kurzhantel" },
  { 
    id: 'ch5', 
    name: 'Liegestütze', 
    muscleGroup: 'BRUST', 
    equipment: 'bodyweight', 
    imageUrl: "https://images.unsplash.com/photo-1598971639058-211a74a96ded?auto=format&fit=crop&w=300&q=80", 
    secondaryMuscles: ["Trizeps", "Core"],
    instructions: "Klassiker. Halte den Körper in einer geraden Linie. Gehe tief runter, bis die Brust fast den Boden berührt.",
    videoUrl: "https://www.youtube.com/results?search_query=liegest%C3%BCtze+technik"
  },
  { id: 'ch6', name: 'Fliegende (Kurzhantel)', muscleGroup: 'BRUST', equipment: 'dumbbell', secondaryMuscles: ["Vordere Schulter"], instructions: "Leichte Beugung im Ellenbogen. Öffne die Arme weit zur Seite, bis du eine Dehnung in der Brust spürst. Führe sie wie eine Umarmung zusammen.", videoUrl: "https://www.youtube.com/results?search_query=dumbbell+flys+deutsch" },
  { id: 'ch7', name: 'Butterfly (Maschine)', muscleGroup: 'BRUST', equipment: 'multigym', secondaryMuscles: ["Vordere Schulter"], instructions: "Setz dich an die Maschine, Rücken gerade. Drücke die Polster/Griffe vor der Brust zusammen. Fokus auf die Kontraktion.", videoUrl: "https://www.youtube.com/results?search_query=butterfly+maschine" },
  { id: 'ch8', name: 'Kabelzug Crossover', muscleGroup: 'BRUST', equipment: 'cable', secondaryMuscles: ["Vordere Schulter"], instructions: "Stehe mittig im Kabelzug. Ziehe die Griffe von oben/unten vor dem Körper zusammen. Leichte Vorlage des Oberkörpers.", videoUrl: "https://www.youtube.com/results?search_query=kabelzug+crossover" },
  { id: 'ch9', name: 'Dips (Vorgebeugt)', muscleGroup: 'BRUST', equipment: 'bodyweight', secondaryMuscles: ["Trizeps", "Vordere Schulter"], instructions: "Beuge dich leicht nach vorne, um die Brust zu treffen. Senke den Körper ab, bis die Schultern unter den Ellenbogen sind.", videoUrl: "https://www.youtube.com/results?search_query=dips+brust" },
  { id: 'ch10', name: 'Brustpresse (Maschine)', muscleGroup: 'BRUST', equipment: 'multigym', secondaryMuscles: ["Trizeps"], instructions: "Drücke die Griffe nach vorne weg. Atme dabei aus. Nicht die Ellenbogen komplett durchstrecken.", videoUrl: "https://www.youtube.com/results?search_query=brustpresse+maschine" },
  
  // --- RÜCKEN ---
  { 
    id: 'ba1', 
    name: 'Kreuzheben (Klassisch)', 
    muscleGroup: 'RÜCKEN', 
    equipment: 'barbell', 
    imageUrl: "https://images.unsplash.com/photo-1517963879433-6ad2b056d712?auto=format&fit=crop&w=300&q=80", 
    secondaryMuscles: ["Beine", "Gesäß", "Unterer Rücken"],
    instructions: "Hüftbreiter Stand. Greife die Stange. Rücken gerade halten! Hebe das Gewicht aus den Beinen und richte dich auf.",
    videoUrl: "https://www.youtube.com/results?search_query=kreuzheben+technik"
  },
  { id: 'ba2', name: 'Klimmzüge (Breit)', muscleGroup: 'RÜCKEN', equipment: 'bodyweight', secondaryMuscles: ["Bizeps"], instructions: "Greife breit. Ziehe dich hoch, bis das Kinn über der Stange ist. Kontrolliert ablassen.", videoUrl: "https://www.youtube.com/results?search_query=klimmz%C3%BCge+lernen" },
  { id: 'ba3', name: 'Latzug zur Brust', muscleGroup: 'RÜCKEN', equipment: 'multigym', secondaryMuscles: ["Bizeps"], instructions: "Breiter Griff. Ziehe die Stange zur oberen Brust. Lehne dich leicht zurück, aber schwinge nicht.", videoUrl: "https://www.youtube.com/results?search_query=latzug+technik" },
  { id: 'ba4', name: 'Latzug (Enger Griff)', muscleGroup: 'RÜCKEN', equipment: 'multigym', secondaryMuscles: ["Bizeps"], instructions: "Nutze den V-Griff. Ziehe zur Brust und spüre den Latissimus.", videoUrl: "https://www.youtube.com/results?search_query=latzug+eng" },
  { id: 'ba5', name: 'Langhantel Rudern (Vorgebeugt)', muscleGroup: 'RÜCKEN', equipment: 'barbell', secondaryMuscles: ["Bizeps", "Hintere Schulter"], instructions: "Oberkörper ca. 45 Grad nach vorne geneigt. Rücken gerade. Ziehe die Hantel zum Bauchnabel.", videoUrl: "https://www.youtube.com/results?search_query=langhantel+rudern" },
  { id: 'ba6', name: 'Einarmiges Kurzhantel-Rudern', muscleGroup: 'RÜCKEN', equipment: 'dumbbell', secondaryMuscles: ["Bizeps"], instructions: "Stütze dich auf einer Bank ab. Ziehe die Hantel seitlich am Körper hoch, Ellenbogen nah am Körper.", videoUrl: "https://www.youtube.com/results?search_query=kurzhantel+rudern" },
  
  // --- BEINE ---
  { 
    id: 'le1', 
    name: 'Kniebeugen (Squat)', 
    muscleGroup: 'BEINE', 
    equipment: 'barbell', 
    imageUrl: "https://images.unsplash.com/photo-1574680096145-d05b474e2155?auto=format&fit=crop&w=300&q=80", 
    secondaryMuscles: ["Gesäß", "Core"],
    instructions: "Stange auf dem Nacken. Schulterbreiter Stand. Gehe tief in die Hocke, Rücken bleibt gerade. Knie nicht nach innen fallen lassen.",
    videoUrl: "https://www.youtube.com/results?search_query=kniebeugen+technik"
  },
  { id: 'le3', name: 'Beinpresse 45°', muscleGroup: 'BEINE', equipment: 'machine', secondaryMuscles: ["Gesäß"], instructions: "Füße mittig auf der Plattform. Gewicht ablassen, bis Knie 90 Grad. Drücke aus den Fersen.", videoUrl: "https://www.youtube.com/results?search_query=beinpresse+technik" },
  { id: 'le5', name: 'Beinstrecker (Maschine)', muscleGroup: 'BEINE', equipment: 'multigym', secondaryMuscles: [], instructions: "Strecke die Beine gegen den Widerstand komplett aus. Kurz oben halten.", videoUrl: "https://www.youtube.com/results?search_query=beinstrecker+maschine" },
  { id: 'le6', name: 'Beinbeuger (Liegend/Sitzend)', muscleGroup: 'BEINE', equipment: 'multigym', secondaryMuscles: [], instructions: "Beuge die Beine so weit wie möglich zum Gesäß. Kontrollierte Bewegung.", videoUrl: "https://www.youtube.com/results?search_query=beinbeuger+maschine" },
  { id: 'le7', name: 'Ausfallschritte (Walking Lunges)', muscleGroup: 'BEINE', equipment: 'dumbbell', secondaryMuscles: ["Gesäß"], instructions: "Große Schritte nach vorne. Hinteres Knie fast zum Boden. Oberkörper aufrecht.", videoUrl: "https://www.youtube.com/results?search_query=ausfallschritte" },
  
  // --- SCHULTERN ---
  { id: 'sh1', name: 'Schulterdrücken (Military Press)', muscleGroup: 'SCHULTERN', equipment: 'barbell', secondaryMuscles: ["Trizeps"], instructions: "Drücke die Langhantel vom Schlüsselbein über den Kopf, bis Arme gestreckt sind. Core anspannen.", videoUrl: "https://www.youtube.com/results?search_query=military+press+technik" },
  { id: 'sh2', name: 'Schulterdrücken (Kurzhantel)', muscleGroup: 'SCHULTERN', equipment: 'dumbbell', secondaryMuscles: ["Trizeps"], instructions: "Sitzend oder stehend. Drücke die Hanteln über Kopf zusammen.", videoUrl: "https://www.youtube.com/results?search_query=schulterdr%C3%BCcken+kurzhantel" },
  { id: 'sh3', name: 'Seitheben (Kurzhantel)', muscleGroup: 'SCHULTERN', equipment: 'dumbbell', secondaryMuscles: [], instructions: "Leichte Beugung im Ellenbogen. Hebe die Arme seitlich bis auf Schulterhöhe.", videoUrl: "https://www.youtube.com/results?search_query=seitheben" },
  { id: 'sh4', name: 'Seitheben (Kabelzug)', muscleGroup: 'SCHULTERN', equipment: 'cable', secondaryMuscles: [], instructions: "Einarmig am Kabelzug. Zieht über den gesamten Bewegungsradius.", videoUrl: "https://www.youtube.com/results?search_query=seitheben+kabelzug" },
  { id: 'sh10', name: 'Upright Row (Aufrechtes Rudern)', muscleGroup: 'SCHULTERN', equipment: 'barbell', secondaryMuscles: ["Nacken", "Bizeps"], instructions: "Ziehe die Stange eng am Körper bis zum Kinn hoch. Ellenbogen zeigen nach oben.", videoUrl: "https://www.youtube.com/results?search_query=upright+row" },
  
  // --- ARME (BIZEPS / TRIZEPS) ---
  { id: 'ar1', name: 'Langhantel Curls', muscleGroup: 'ARME', equipment: 'barbell', secondaryMuscles: [], instructions: "Schulterbreiter Griff. Nur die Unterarme bewegen sich. Ellenbogen am Körper fixieren.", videoUrl: "https://www.youtube.com/results?search_query=langhantel+curls" },
  { id: 'ar2', name: 'Kurzhantel Curls (Stehend)', muscleGroup: 'ARME', equipment: 'dumbbell', secondaryMuscles: [], instructions: "Eindrehen der Handgelenke beim Hochgehen (Supination) für max. Kontraktion.", videoUrl: "https://www.youtube.com/results?search_query=kurzhantel+curls" },
  { id: 'ar3', name: 'Hammer Curls', muscleGroup: 'ARME', equipment: 'dumbbell', secondaryMuscles: ["Unterarme"], instructions: "Daumen zeigt nach oben. Neutraler Griff.", videoUrl: "https://www.youtube.com/results?search_query=hammer+curls" },
  { id: 'ar7', name: 'Trizeps Drücken (Kabelzug)', muscleGroup: 'ARME', equipment: 'cable', secondaryMuscles: [], instructions: "Oberarme bleiben fixiert. Drücke das Seil/Stange nach unten.", videoUrl: "https://www.youtube.com/results?search_query=trizeps+dr%C3%BCcken+kabel" },
  { id: 'ar8', name: 'French Press (Skullcrushers)', muscleGroup: 'ARME', equipment: 'barbell', secondaryMuscles: [], instructions: "Liegend. Senke die Stange zur Stirn ab und drücke sie aus dem Trizeps wieder hoch.", videoUrl: "https://www.youtube.com/results?search_query=french+press+trizeps" },
  { id: 'ar12', name: 'Dips (Barren)', muscleGroup: 'ARME', equipment: 'bodyweight', secondaryMuscles: ["Brust"], instructions: "Körper aufrecht halten für Trizeps-Fokus.", videoUrl: "https://www.youtube.com/results?search_query=dips+trizeps" },
  
  // --- RUMPF (BAUCH / CORE) ---
  { id: 'ab1', name: 'Crunches', muscleGroup: 'RUMPF', equipment: 'bodyweight', secondaryMuscles: [], instructions: "Hebe nur die Schulterblätter vom Boden ab. Spanne den Bauch fest an.", videoUrl: "https://www.youtube.com/results?search_query=crunches+richtig" },
  { id: 'ab3', name: 'Plank (Unterarmstütz)', muscleGroup: 'RUMPF', equipment: 'bodyweight', secondaryMuscles: ["Schultern"], instructions: "Körper bildet eine Linie. Po nicht zu hoch, nicht durchhängen. Spannung halten.", videoUrl: "https://www.youtube.com/results?search_query=plank+technik" },
  { id: 'ab6', name: 'Kabel-Crunches (Kneeling)', muscleGroup: 'RUMPF', equipment: 'cable', secondaryMuscles: [], instructions: "Knie dich hin, Seil im Nacken. Rolle dich ein und bringe die Ellenbogen Richtung Knie.", videoUrl: "https://www.youtube.com/results?search_query=kabel+crunches" },

  // --- CARDIO ---
  { id: 'ca1', name: 'Radfahren (Stationär)', muscleGroup: 'AUSDAUER', equipment: 'hometrainer', secondaryMuscles: ["Beine"], instructions: "Stelle den Sitz so ein, dass das Bein fast gestreckt ist.", videoUrl: "https://www.youtube.com/results?search_query=spinning+technik" },
  { id: 'ca2', name: 'Ellipsentrainer', muscleGroup: 'AUSDAUER', equipment: 'crosstrainer', secondaryMuscles: ["Ganzkörper"], instructions: "Nutze die Armstangen aktiv mit für Ganzkörpertraining.", videoUrl: null },

  // NEUE ÜBUNGEN FÜR MULTIGYM (Kettler Fitmaster)
  { id: 'm1', name: 'Latzug (Maschine)', muscleGroup: 'RÜCKEN', equipment: 'multigym', secondaryMuscles: ["Bizeps"], instructions: "Ziehe die Stange zur oberen Brust. Schulterblätter zusammenziehen.", videoUrl: "https://www.youtube.com/results?search_query=latzug+maschine" },
  { id: 'm2', name: 'Brustpresse (Maschine)', muscleGroup: 'BRUST', equipment: 'multigym', secondaryMuscles: ["Trizeps"], instructions: "Ellenbogen nicht ganz durchstrecken. Kontrolliertes Tempo.", videoUrl: "https://www.youtube.com/results?search_query=brustpresse+kettler" },
  { id: 'm3', name: 'Beincurl (Maschine)', muscleGroup: 'BEINE', equipment: 'multigym', secondaryMuscles: [], instructions: "Stehend oder liegend (je nach Modell). Ferse zum Gesäß ziehen.", videoUrl: "https://www.youtube.com/results?search_query=beincurl+stehend+kabelzug" },
  { id: 'm4', name: 'Beinstrecken (Maschine)', muscleGroup: 'BEINE', equipment: 'multigym', secondaryMuscles: [], instructions: "Strecke die Beine gegen das Polster. Oben kurz halten.", videoUrl: "https://www.youtube.com/results?search_query=beinstrecker+maschine" },
  { id: 'm5', name: 'Butterfly (Maschine)', muscleGroup: 'BRUST', equipment: 'multigym', secondaryMuscles: ["Vordere Schulter"], instructions: "Arme fast gestreckt. Führe die Polster vor der Brust zusammen.", videoUrl: "https://www.youtube.com/results?search_query=butterfly+fitnessger%C3%A4t" },
  { id: 'm6', name: 'Rudern (Maschine/Kabel)', muscleGroup: 'RÜCKEN', equipment: 'multigym', secondaryMuscles: ["Bizeps"], instructions: "Unteren Kabelzug nutzen (falls vorhanden) oder Ruderarm. Rücken gerade.", videoUrl: "https://www.youtube.com/results?search_query=rudern+am+kabelzug" },
  { id: 'm7', name: 'Kabelzug Trizeps Pushdown', muscleGroup: 'ARME', equipment: 'multigym', secondaryMuscles: [], instructions: "Oberer Kabelzug. Ellenbogen am Körper fixieren, nur Unterarm bewegt sich.", videoUrl: "https://www.youtube.com/results?search_query=trizeps+pushdown" },
  { id: 'm8', name: 'Bizeps Curls (Kabel)', muscleGroup: 'ARME', equipment: 'multigym', secondaryMuscles: [], instructions: "Unterer Kabelzug. Stange oder Seil greifen und curlen.", videoUrl: "https://www.youtube.com/results?search_query=kabel+bizeps+curls" }
];

const OFFLINE_QUOTES = [
  { text: "Der einzige schlechte Workout ist der, der nicht stattgefunden hat.", author: "Unknown" },
  { text: "Schmerz ist Schwäche, die den Körper verlässt.", author: "US Marines" },
  { text: "Mach es für das 'Danach'-Gefühl.", author: "FitTrack" },
  { text: "Disziplin ist nur die Wahl zwischen dem was du jetzt willst und dem was du am meisten willst.", author: "Abraham Lincoln" }
];

// --- Helper Functions ---

const generateId = () => Math.random().toString(36).substr(2, 9);
const formatDate = (isoString: string) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};
const getDayName = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('de-DE', { weekday: 'long' });
};
const getTodayDateString = () => new Date().toISOString().split('T')[0];
const getAge = (birthDate: string) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};
const getExerciseImage = (template: ExerciseTemplate) => {
  if (template.imageUrl) return template.imageUrl;
  return MUSCLE_IMAGES[template.muscleGroup] || "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=300&q=80";
};
const formatRestTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};
const calculateBMI = (weight: number, heightCm: number) => {
  if (!weight || !heightCm) return null;
  const heightM = heightCm / 100;
  return (weight / (heightM * heightM)).toFixed(1);
};
const resizeImage = (file: File, maxWidth: number = 600, quality: number = 0.7): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const elem = document.createElement('canvas');
        const scaleFactor = maxWidth / img.width;
        if (img.width > maxWidth) {
            elem.width = maxWidth;
            elem.height = img.height * scaleFactor;
        } else {
            elem.width = img.width;
            elem.height = img.height;
        }
        const ctx = elem.getContext('2d');
        ctx?.drawImage(img, 0, 0, elem.width, elem.height);
        const dataUrl = elem.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

// --- Sub-Components ---
interface MuscleHeatmapProps {
  history: WorkoutSession[];
}
const MuscleHeatmap: React.FC<MuscleHeatmapProps> = ({ history }) => {
  const [muscleVolume, setMuscleVolume] = useState<Record<string, number>>({});
  useEffect(() => {
    const vol: Record<string, number> = { BRUST: 0, RÜCKEN: 0, BEINE: 0, SCHULTERN: 0, ARME: 0, RUMPF: 0, AUSDAUER: 0 };
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    history.forEach(session => {
      const sessionDate = new Date(session.date);
      if (sessionDate >= oneWeekAgo) {
        session.exercises.forEach(ex => {
          if (vol[ex.muscleGroup] !== undefined) { vol[ex.muscleGroup] += ex.sets.filter(s => s.completed).length; }
        });
      }
    });
    setMuscleVolume(vol);
  }, [history]);
  const getIntensityColor = (sets: number) => {
    if (sets === 0) return 'bg-neutral-800 text-neutral-600 border-neutral-700';
    if (sets < 5) return 'bg-yellow-900/30 text-yellow-500 border-yellow-700/50';
    if (sets < 10) return 'bg-lime-900/30 text-lime-400 border-lime-500/50';
    return 'bg-purple-900/30 text-purple-400 border-purple-500/50 shadow-[0_0_10px_rgba(168,85,247,0.2)]';
  };
  const getLabel = (sets: number) => {
    if (sets === 0) return 'Ruhe';
    if (sets < 5) return 'Aktiv';
    if (sets < 10) return 'Optimal';
    return 'Maximal';
  };
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 mb-4">
      <h3 className="text-white font-bold uppercase text-xs tracking-widest mb-4 flex items-center gap-2"><Flame size={16} className="text-orange-500" /> Muscle Heatmap (7 Tage)</h3>
      <div className="grid grid-cols-2 gap-3">
         {Object.entries(muscleVolume).map(([muscle, sets]) => (
           <div key={muscle} className={`border rounded-lg p-3 flex justify-between items-center ${getIntensityColor(sets)}`}>
              <div><div className="font-bold text-xs uppercase">{muscle}</div><div className="text-[9px] opacity-70 font-mono">{sets} Sätze</div></div>
              <div className="text-[9px] font-bold uppercase tracking-widest bg-black/30 px-2 py-1 rounded">{getLabel(sets)}</div>
           </div>
         ))}
      </div>
    </div>
  );
};

const PlateCalculator = () => {
  const [targetWeight, setTargetWeight] = useState(60);
  const [barWeight, setBarWeight] = useState(20);
  const [plates, setPlates] = useState<number[]>([]);
  useEffect(() => {
    let remaining = (targetWeight - barWeight) / 2;
    const availablePlates = [25, 20, 15, 10, 5, 2.5, 1.25];
    const calculated: number[] = [];
    if (remaining < 0) { setPlates([]); return; }
    for (const plate of availablePlates) {
      while (remaining >= plate) { calculated.push(plate); remaining -= plate; }
    }
    setPlates(calculated);
  }, [targetWeight, barWeight]);
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 mb-4">
       <h3 className="text-lime-400 font-bold uppercase text-xs tracking-widest mb-4 flex items-center gap-2"><Disc size={16} /> Platten-Rechner</h3>
       <div className="grid grid-cols-2 gap-4 mb-6">
          <div><label className="text-[9px] text-neutral-500 font-bold uppercase block mb-1">Zielgewicht (kg)</label><input type="number" value={targetWeight} onChange={(e) => setTargetWeight(parseFloat(e.target.value) || 0)} className="w-full bg-black border border-neutral-700 rounded-lg p-3 text-white font-mono font-bold text-center focus:border-lime-500 outline-none" /></div>
          <div><label className="text-[9px] text-neutral-500 font-bold uppercase block mb-1">Stange (kg)</label><div className="flex gap-2"><button onClick={() => setBarWeight(20)} className={`flex-1 py-2 rounded text-xs font-bold border ${barWeight === 20 ? 'bg-lime-900/30 border-lime-500 text-lime-400' : 'bg-black border-neutral-800 text-neutral-500'}`}>20</button><button onClick={() => setBarWeight(10)} className={`flex-1 py-2 rounded text-xs font-bold border ${barWeight === 10 ? 'bg-lime-900/30 border-lime-500 text-lime-400' : 'bg-black border-neutral-800 text-neutral-500'}`}>10</button></div></div>
       </div>
       <div className="bg-black/50 rounded-lg p-4 flex items-center justify-center min-h-[100px] border border-neutral-800 relative overflow-hidden">
          <div className="h-2 bg-neutral-600 w-full absolute top-1/2 -translate-y-1/2 z-0"></div><div className="h-16 w-4 bg-neutral-400 z-10 mr-1 rounded-sm border border-neutral-600"></div>
          <div className="flex items-center gap-1 z-10">
             {plates.length === 0 && <span className="text-neutral-600 text-xs font-mono">Leer</span>}
             {plates.map((p, i) => {
               let h = 'h-24'; let color = 'bg-red-600';
               if (p === 20) { h = 'h-24'; color = 'bg-blue-600'; } else if (p === 15) { h = 'h-20'; color = 'bg-yellow-500'; } else if (p === 10) { h = 'h-16'; color = 'bg-green-600'; } else if (p === 5) { h = 'h-12'; color = 'bg-white'; } else { h = 'h-10'; color = 'bg-neutral-500'; }
               return <div key={i} className={`${h} w-3 ${color} rounded-sm border border-black/30 shadow-lg flex items-center justify-center group relative`}><span className="absolute -top-6 text-[9px] text-white bg-black px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">{p}</span></div>
             })}
          </div>
       </div>
    </div>
  );
};

const OneRepMaxCalculator = () => {
  const [weight, setWeight] = useState(''); const [reps, setReps] = useState(''); const [oneRM, setOneRM] = useState<number | null>(null);
  useEffect(() => {
    const w = parseFloat(weight); const r = parseFloat(reps);
    if (w > 0 && r > 0) { const res = w * (1 + r / 30); setOneRM(Math.round(res)); } else { setOneRM(null); }
  }, [weight, reps]);
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5">
       <h3 className="text-lime-400 font-bold uppercase text-xs tracking-widest mb-4 flex items-center gap-2"><Hash size={16} /> 1RM Schätzer</h3>
       <div className="grid grid-cols-2 gap-4 mb-4">
         <div><label className="text-[9px] text-neutral-500 font-bold uppercase block mb-1">Gewicht (kg)</label><input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} className="w-full bg-black border border-neutral-700 rounded-lg p-3 text-white font-mono text-center focus:border-lime-500 outline-none" placeholder="0" /></div>
         <div><label className="text-[9px] text-neutral-500 font-bold uppercase block mb-1">Wdh.</label><input type="number" value={reps} onChange={(e) => setReps(e.target.value)} className="w-full bg-black border border-neutral-700 rounded-lg p-3 text-white font-mono text-center focus:border-lime-500 outline-none" placeholder="0" /></div>
       </div>
       <div className="bg-neutral-800/50 rounded-lg p-4 flex justify-between items-center border border-neutral-800"><span className="text-xs text-neutral-400 uppercase font-bold">Est. 1RM</span><span className="text-2xl font-black text-white">{oneRM ? `${oneRM} kg` : '-'}</span></div>
    </div>
  );
};

interface WeightChartProps { data: BodyStat[]; }
const WeightChart: React.FC<WeightChartProps> = ({ data }) => {
  if (!data || data.length < 2) return <div className="h-32 flex items-center justify-center text-xs text-neutral-600 bg-neutral-900/50 rounded-lg border border-neutral-800 border-dashed">Nicht genügend Daten für Diagramm</div>;
  const width = 300; const height = 100; const padding = 5;
  const sortedData = [...data].sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(-10);
  const minWeight = Math.min(...sortedData.map(d => d.weight)) - 2;
  const maxWeight = Math.max(...sortedData.map(d => d.weight)) + 2;
  const getX = (index: number) => (index / (sortedData.length - 1)) * (width - 2 * padding) + padding;
  const getY = (weight: number) => height - ((weight - minWeight) / (maxWeight - minWeight)) * (height - 2 * padding) - padding;
  const points = sortedData.map((d, i) => `${getX(i)},${getY(d.weight)}`).join(' ');
  return (
    <div className="w-full h-32 bg-neutral-900/50 rounded-xl border border-neutral-800 p-2 relative overflow-hidden mb-4">
       <div className="absolute top-2 right-2 text-[10px] uppercase font-bold text-neutral-600 flex items-center gap-1"><Scale size={10}/> Gewichtstrend</div>
       <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none"><polyline fill="none" stroke="#a3e635" strokeWidth="2" points={points} vectorEffect="non-scaling-stroke" />{sortedData.map((d, i) => (<circle key={i} cx={getX(i)} cy={getY(d.weight)} r="3" fill="#a3e635" />))}</svg>
       <div className="absolute top-1 left-2 text-[9px] text-neutral-500 font-mono">{maxWeight.toFixed(0)}kg</div>
       <div className="absolute bottom-1 left-2 text-[9px] text-neutral-500 font-mono">{minWeight.toFixed(0)}kg</div>
    </div>
  );
}

interface VolumeChartProps { data: WorkoutSession[]; }
const VolumeChart: React.FC<VolumeChartProps> = ({ data }) => {
  if (!data || data.length < 2) return <div className="h-32 flex items-center justify-center text-xs text-neutral-600 bg-neutral-900/50 rounded-lg border border-neutral-800 border-dashed mb-6">Trainiere mehr für erste Analysen</div>;
  const width = 300; const height = 100; const padding = 10;
  const sortedData = [...data].sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(-7);
  const maxVol = Math.max(...sortedData.map(d => d.totalVolume));
  const scaleY = maxVol > 0 ? (height - 2 * padding) / maxVol : 0;
  return (
    <div className="w-full h-40 bg-neutral-900/50 rounded-xl border border-neutral-800 p-4 relative overflow-hidden mb-6">
       <div className="flex justify-between items-start mb-2"><h3 className="text-xs font-bold text-white uppercase flex items-center gap-2"><BarChart3 size={14} className="text-orange-400"/> Trainingsvolumen</h3><span className="text-[10px] text-neutral-500 font-mono">LETZTE 7 WORKOUTS</span></div>
       <div className="relative w-full h-24 flex items-end justify-between gap-1">
          {sortedData.map((d, i) => {
             const barHeight = d.totalVolume * scaleY;
             return <div key={d.id} className="flex flex-col items-center gap-1 w-full"><div className="w-full bg-gradient-to-t from-orange-600 to-orange-400 rounded-t-sm opacity-80 hover:opacity-100 transition-all" style={{ height: `${Math.max(barHeight, 4)}px` }}></div><div className="text-[8px] text-neutral-600 font-mono">{new Date(d.date).getDate()}.{new Date(d.date).getMonth()+1}</div></div>
          })}
          <div className="absolute top-0 left-0 w-full border-t border-neutral-800 border-dashed"></div>
          <div className="absolute top-0 right-0 text-[8px] text-neutral-600 font-mono transform -translate-y-1/2 bg-neutral-900 px-1">{(maxVol/1000).toFixed(1)}t</div>
       </div>
    </div>
  );
}

interface EditProfileModalProps {
  isOpen: boolean; onClose: () => void; userPhoto: string | null; onPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void; userName: string; setUserName: (name: string) => void; userHeight: string; setUserHeight: (height: string) => void; userBirthDate: string; setUserBirthDate: (date: string) => void; onSave: () => void;
}
const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose, userPhoto, onPhotoUpload, userName, setUserName, userHeight, setUserHeight, userBirthDate, setUserBirthDate, onSave }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl w-full max-w-xs p-6 shadow-2xl space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-white font-bold uppercase text-center mb-4">Profil bearbeiten</h3>
        <div className="flex flex-col items-center">
          <div className="w-20 h-20 rounded-full bg-neutral-800 border-2 border-neutral-700 flex items-center justify-center overflow-hidden relative group">
            {userPhoto ? <img src={userPhoto} alt="Profile" className="w-full h-full object-cover" /> : <User size={32} className="text-neutral-500" />}
            <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"><Camera size={20} className="text-white" /><input type="file" accept="image/*" className="hidden" onChange={onPhotoUpload} /></label>
          </div>
          <span className="text-[10px] text-neutral-500 mt-2">Klicken zum Ändern</span>
        </div>
        <div className="space-y-3">
          <div><label className="text-xs text-neutral-500 uppercase font-bold mb-1 block">Dein Name</label><input value={userName} onChange={(e) => setUserName(e.target.value)} className="w-full bg-black border border-neutral-700 rounded-lg p-3 text-white focus:border-lime-500 outline-none text-sm" placeholder="Name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-neutral-500 uppercase font-bold mb-1 block">Größe</label><input type="number" value={userHeight} onChange={(e) => setUserHeight(e.target.value)} className="w-full bg-black border border-neutral-700 rounded-lg p-3 text-white focus:border-lime-500 outline-none text-sm" placeholder="180" /></div>
            <div><label className="text-xs text-neutral-500 uppercase font-bold mb-1 block">Geb. Datum</label><input type="date" value={userBirthDate} onChange={(e) => setUserBirthDate(e.target.value)} className="w-full bg-black border border-neutral-700 rounded-lg p-3 text-white focus:border-lime-500 outline-none text-sm" /></div>
          </div>
        </div>
        <button onClick={onSave} className="w-full bg-lime-500 hover:bg-lime-400 text-black font-bold py-3 rounded-lg uppercase tracking-wide transition-colors">Speichern</button>
      </div>
    </div>
  );
};

interface RestTimerOverlayProps {
  isResting: boolean; restTimer: number; setRestTimer: React.Dispatch<React.SetStateAction<number>>; setIsResting: React.Dispatch<React.SetStateAction<boolean>>;
}
const RestTimerOverlay: React.FC<RestTimerOverlayProps> = ({ isResting, restTimer, setRestTimer, setIsResting }) => {
  if (!isResting) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-neutral-900 border-t border-lime-500/50 p-4 z-50 animate-in slide-in-from-bottom-5 shadow-[0_-5px_20px_rgba(0,0,0,0.5)]">
      <div className="max-w-md mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3"><div className="bg-lime-500/20 p-2 rounded-full animate-pulse"><Timer className="text-lime-400" size={24} /></div><div><div className="text-lime-400 font-black text-2xl font-mono leading-none">{formatRestTime(restTimer)}</div><div className="text-neutral-500 text-[9px] font-bold uppercase tracking-widest">Ruhephase</div></div></div>
        <div className="flex gap-2"><button onClick={() => setRestTimer(prev => prev + 30)} className="bg-neutral-800 text-white px-4 py-2 rounded-lg font-bold text-xs uppercase hover:bg-neutral-700 transition-colors border border-neutral-700">+30s</button><button onClick={() => setIsResting(false)} className="bg-white text-black px-4 py-2 rounded-lg font-bold text-xs uppercase hover:bg-gray-200 transition-colors flex items-center gap-1">Skip <FastForward size={12} fill="black" /></button></div>
      </div>
      <div className="absolute top-0 left-0 h-1 bg-lime-500 transition-all duration-1000 ease-linear" style={{ width: `${(restTimer / 60) * 100}%` }}></div>
    </div>
  );
};

interface ExerciseInfoModalProps {
  showInfoModal: ExerciseTemplate | null; setShowInfoModal: React.Dispatch<React.SetStateAction<ExerciseTemplate | null>>; onUpdateImage: (file: File) => Promise<void>; isUploading: boolean;
}
const ExerciseInfoModal: React.FC<ExerciseInfoModalProps> = ({ showInfoModal, setShowInfoModal, onUpdateImage, isUploading }) => {
  if (!showInfoModal) return null;
  const equipmentLabel = EQUIPMENT_LIST.find(e => e.id === showInfoModal.equipment)?.label;
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.files && e.target.files[0]) { onUpdateImage(e.target.files[0]); } };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setShowInfoModal(null)}>
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl w-full max-w-sm max-h-[80vh] overflow-y-auto flex flex-col relative shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="h-48 w-full relative group">
           <img src={getExerciseImage(showInfoModal)} className="w-full h-full object-cover" alt={showInfoModal.name} />
           <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-neutral-900 to-transparent"></div>
           <h2 className="absolute bottom-4 left-4 text-xl font-black italic uppercase text-white drop-shadow-md">{showInfoModal.name}</h2>
           <label className="absolute top-4 right-14 bg-black/60 hover:bg-lime-500/80 rounded-full p-2 z-50 backdrop-blur-md transition-all shadow-lg border border-white/10 cursor-pointer">{isUploading ? <Loader2 size={20} className="text-white animate-spin" /> : <Edit3 size={20} className="text-white" />}<input type="file" accept="image/*" className="hidden" onChange={handleFileChange} disabled={isUploading} /></label>
        </div>
        <div className="p-5 space-y-6">
           <div><h4 className="text-lime-400 text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-2"><Dna size={14} /> Zielmuskeln</h4><div className="flex flex-wrap gap-2"><span className="bg-lime-900/30 border border-lime-500/30 text-lime-400 px-3 py-1 rounded text-xs font-bold uppercase">{showInfoModal.muscleGroup} (Primär)</span>{showInfoModal.secondaryMuscles?.map(m => <span key={m} className="bg-neutral-800 text-neutral-400 px-3 py-1 rounded text-xs font-medium">{m}</span>)}</div></div>
           <div><h4 className="text-lime-400 text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-2"><Dumbbell size={14} /> Equipment</h4><span className="bg-neutral-800 text-white px-3 py-1 rounded text-xs font-medium border border-neutral-700">{equipmentLabel}</span></div>
           <div><h4 className="text-lime-400 text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-2"><BookOpen size={14} /> Ausführung</h4><p className="text-neutral-300 text-sm leading-relaxed">{showInfoModal.instructions || "Keine detaillierte Anleitung verfügbar."}</p></div>
           <div><h4 className="text-lime-400 text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-2"><Youtube size={14} /> Video-Anleitung</h4>{showInfoModal.videoUrl ? (<a href={showInfoModal.videoUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-neutral-800 hover:bg-neutral-700 p-3 rounded-xl transition-colors group"><div className="bg-red-600 text-white p-2 rounded-lg group-hover:scale-110 transition-transform"><Play size={16} fill="white" /></div><span className="text-sm font-bold text-white">Tutorial auf YouTube suchen</span></a>) : (<div className="text-neutral-500 text-xs italic">Kein Video verknüpft.</div>)}</div>
        </div>
        <button onClick={() => setShowInfoModal(null)} className="absolute top-4 right-4 text-white bg-black/60 hover:bg-red-500/80 rounded-full p-2 z-50 backdrop-blur-md transition-all shadow-lg border border-white/10"><X size={20} /></button>
      </div>
    </div>
  )
};

interface ConfirmModalProps { isOpen: boolean; onConfirm: () => void; onCancel: () => void; title: string; message: string; confirmText?: string; isDestructive?: boolean; }
const ConfirmModal: React.FC<ConfirmModalProps> = ({ isOpen, onConfirm, onCancel, title, message, confirmText = 'Bestätigen', isDestructive = false }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200" onClick={onCancel}>
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl w-full max-w-xs p-6 shadow-2xl space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex flex-col items-center text-center"><div className={`p-3 rounded-full mb-3 ${isDestructive ? 'bg-red-900/20 text-red-500' : 'bg-lime-900/20 text-lime-400'}`}>{isDestructive ? <Trash2 size={32} /> : <Info size={32} />}</div><h3 className="text-white font-bold text-lg mb-1">{title}</h3><p className="text-neutral-400 text-sm">{message}</p></div>
        <div className="flex gap-2"><button onClick={onCancel} className="flex-1 bg-neutral-800 text-neutral-300 font-bold py-3 rounded-lg hover:bg-neutral-700 transition-colors">Abbrechen</button><button onClick={onConfirm} className={`flex-1 font-bold py-3 rounded-lg transition-colors ${isDestructive ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-lime-500 text-black hover:bg-lime-400'}`}>{confirmText}</button></div>
      </div>
    </div>
  );
};

// --- Main App Component ---

export default function App() {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'workout' | 'history' | 'profile' | 'plan'>('dashboard');
  const [profileSubTab, setProfileSubTab] = useState<'stats' | 'equipment' | 'library' | 'tools'>('stats'); 
  const [history, setHistory] = useState<WorkoutSession[]>([]);
  const [userEquipment, setUserEquipment] = useState<EquipmentType[]>(['bodyweight', 'dumbbell', 'barbell', 'bench', 'multigym', 'cable', 'incline_bench']); 
  const [bodyStats, setBodyStats] = useState<BodyStat[]>([]);
  const [fitnessLevel, setFitnessLevel] = useState<FitnessLevel>('Intermediate'); 
  const [exerciseLibrary, setExerciseLibrary] = useState<ExerciseTemplate[]>(INITIAL_EXERCISES);
  const [weeklyPlan, setWeeklyPlan] = useState<PlannedWorkout[]>([]);
  const [supplements, setSupplements] = useState<Supplement[]>([]);
  const [userName, setUserName] = useState('JD');
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [userHeight, setUserHeight] = useState(''); 
  const [userBirthDate, setUserBirthDate] = useState(''); 
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [isEditingBasicStats, setIsEditingBasicStats] = useState(false);
  const [activeWorkout, setActiveWorkout] = useState<WorkoutSession | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0); 
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [restTimer, setRestTimer] = useState(0);
  const [isResting, setIsResting] = useState(false);
  const DEFAULT_REST_TIME = 60;
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [dailyQuote, setDailyQuote] = useState<QuoteData | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [editingExerciseId, setEditingExerciseId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState<ExerciseTemplate | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['Basis', 'Freihanteln', 'Bänke', 'Maschinen', 'Cardio']);
  const [confirmModalState, setConfirmModalState] = useState<{ isOpen: boolean; title: string; message: string; onConfirm: () => void; isDestructive?: boolean; confirmText?: string; }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [newWeight, setNewWeight] = useState('');
  const [newBodyFat, setNewBodyFat] = useState('');
  const [newMuscleMass, setNewMuscleMass] = useState('');
  const [newSuppName, setNewSuppName] = useState('');
  const [newSuppDosage, setNewSuppDosage] = useState('');
  const [newSuppTime, setNewSuppTime] = useState('12:00'); 

  // --- Handlers defined BEFORE render functions ---
  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') { setDeferredPrompt(null); }
  };

  const fetchDailyMotivation = async () => {
    setIsLoadingQuote(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); 
      const response = await fetch('https://api.quotable.io/random?tags=inspirational,fitness', { signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.ok) {
        const data = await response.json();
        setDailyQuote({ text: data.content, author: data.author });
      } else { throw new Error('API Error'); }
    } catch (error) {
      const randomQuote = OFFLINE_QUOTES[Math.floor(Math.random() * OFFLINE_QUOTES.length)];
      setDailyQuote(randomQuote);
    } finally { setIsLoadingQuote(false); }
  };

  const saveProfileDetails = async () => {
    if (!user) return;
    await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'profile'), { 
      name: userName, height: userHeight, birthDate: userBirthDate
    }, { merge: true });
    setShowProfileEdit(false);
  };

  const handleProfilePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsUploading(true);
      try {
        const resizedBase64 = await resizeImage(file, 400, 0.7);
        setUserPhoto(resizedBase64);
        if (user) { await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'profile'), { photo: resizedBase64 }, { merge: true }); }
      } catch (err) { console.error("Profile photo upload failed", err); } finally { setIsUploading(false); }
    }
  };

  const updateBasicStats = async () => {
    if (!user) return;
    await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'profile'), {
        height: userHeight, birthDate: userBirthDate
    }, { merge: true });
    setIsEditingBasicStats(false);
  }

  const addBodyStat = async () => {
    if (!newWeight || !user) return;
    const stat: BodyStat = {
      id: generateId(), date: new Date().toISOString(), weight: parseFloat(newWeight),
      bodyFat: newBodyFat ? parseFloat(newBodyFat) : null, muscleMass: newMuscleMass ? parseFloat(newMuscleMass) : null,
    };
    await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'stats', stat.id), stat);
    setNewWeight(''); setNewBodyFat(''); setNewMuscleMass('');
  };

  const addSupplement = async () => {
      if (!newSuppName || !newSuppDosage || !newSuppTime || !user) return;
      const newSupplement: Supplement = {
          id: generateId(), name: newSuppName, dosage: newSuppDosage, time: newSuppTime, lastTaken: null
      };
      await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'supplements', newSupplement.id), newSupplement);
      setNewSuppName(''); setNewSuppDosage('');
  };

  const logSupplementTaken = async (supp: Supplement) => {
      if (!user) return;
      const now = new Date().toISOString();
      await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'supplements', supp.id), { ...supp, lastTaken: now }, { merge: true });
  };

  const deleteSupplement = async (id: string) => {
      if (user) await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'supplements', id));
  };

  const handleExerciseImageUpdate = async (file: File) => {
     if (!showInfoModal || !user) return;
     setIsUploading(true);
     try {
       const resizedBase64 = await resizeImage(file, 600, 0.7);
       setExerciseLibrary(prev => prev.map(ex => ex.id === showInfoModal.id ? { ...ex, imageUrl: resizedBase64, isCustom: true } : ex));
       setShowInfoModal(prev => prev ? { ...prev, imageUrl: resizedBase64 } : null);
       const updatedExercise: ExerciseTemplate = { ...showInfoModal, imageUrl: resizedBase64, isCustom: true };
       await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'custom_exercises', updatedExercise.id), updatedExercise);
     } catch (err) { console.error("Exercise image upload failed", err); } finally { setIsUploading(false); }
  };

  const saveExerciseEdit = async () => {
    if (!editingExerciseId || !user) return;
    const original = exerciseLibrary.find(e => e.id === editingExerciseId);
    if (!original) return;
    const updatedExercise: ExerciseTemplate = { ...original, name: editName, imageUrl: editUrl || null, isCustom: true };
    await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'custom_exercises', updatedExercise.id), updatedExercise);
    setEditingExerciseId(null);
  };
  
  const cancelEdit = () => { setEditingExerciseId(null); };
  const startEditingExercise = (ex: ExerciseTemplate) => { setEditingExerciseId(ex.id); setEditName(ex.name); setEditUrl(ex.imageUrl || ''); };

  const getLastPerformance = (exerciseName: string): string | null => {
    for (const session of history) {
      const ex = session.exercises.find(e => e.name === exerciseName);
      if (ex && ex.sets.length > 0) {
        const bestSet = ex.sets.reduce((prev, current) => (prev.weight > current.weight) ? prev : current);
        return `${bestSet.weight}kg × ${bestSet.reps}`;
      }
    }
    return null;
  };
  
  const addExerciseToWorkout = (template: ExerciseTemplate) => {
    if (!activeWorkout) return;
    const newExercise: Exercise = {
      id: generateId(), name: template.name, muscleGroup: template.muscleGroup, equipment: template.equipment,
      imageUrl: getExerciseImage(template), instructions: template.instructions || null, videoUrl: template.videoUrl || null,
      secondaryMuscles: template.secondaryMuscles || [], sets: [{ id: generateId(), reps: 10, weight: 0, completed: false }]
    };
    setActiveWorkout({ ...activeWorkout, exercises: [...activeWorkout.exercises, newExercise] });
    setSearchTerm('');
  };

  const updateSet = (exerciseId: string, setId: string, field: keyof Set, value: any) => {
    if (!activeWorkout) return;
    if (field === 'completed' && value === true) { setRestTimer(DEFAULT_REST_TIME); setIsResting(true); }
    const updatedExercises = activeWorkout.exercises.map(ex => {
      if (ex.id !== exerciseId) return ex;
      const updatedSets = ex.sets.map(set => set.id !== setId ? set : { ...set, [field]: value });
      return { ...ex, sets: updatedSets };
    });
    setActiveWorkout({ ...activeWorkout, exercises: updatedExercises });
  };

  const addSet = (exerciseId: string) => {
    if (!activeWorkout) return;
    const updatedExercises = activeWorkout.exercises.map(ex => {
      if (ex.id !== exerciseId) return ex;
      const previousSet = ex.sets[ex.sets.length - 1];
      const newSet: Set = { id: generateId(), reps: previousSet ? previousSet.reps : 10, weight: previousSet ? previousSet.weight : 0, completed: false };
      return { ...ex, sets: [...ex.sets, newSet] };
    });
    setActiveWorkout({ ...activeWorkout, exercises: updatedExercises });
  };

  const removeSet = (exerciseId: string, setId: string) => {
    if (!activeWorkout) return;
    const updatedExercises = activeWorkout.exercises.map(ex => {
      if (ex.id !== exerciseId) return ex;
      return { ...ex, sets: ex.sets.filter(s => s.id !== setId) };
    }).filter(ex => ex.sets.length > 0);
    setActiveWorkout({ ...activeWorkout, exercises: updatedExercises });
  };

  const startWorkout = () => {
    setActiveWorkout({
      id: generateId(), date: new Date().toISOString(), name: `TRAINING ${new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
      duration: 0, exercises: [], totalVolume: 0
    });
    setActiveTab('workout');
  };

  const finishWorkout = async () => {
    if (!activeWorkout || !user) return;
    const sanitizedExercises = activeWorkout.exercises.map(ex => ({
        ...ex, imageUrl: ex.imageUrl || null, instructions: ex.instructions || null, videoUrl: ex.videoUrl || null, secondaryMuscles: ex.secondaryMuscles || []
    }));
    const finishedWorkout = {
      ...activeWorkout, exercises: sanitizedExercises, duration: Math.round(elapsedTime / 60),
      totalVolume: activeWorkout.exercises.reduce((total, ex) => total + ex.sets.reduce((setTotal, set) => setTotal + (set.weight * set.reps), 0), 0)
    };
    await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'history', finishedWorkout.id), finishedWorkout);
    const todayStr = getTodayDateString();
    const todaysPlan = weeklyPlan.find(p => p.date === todayStr);
    if (todaysPlan && !todaysPlan.completed && todaysPlan.workoutTemplate) {
        const updatedPlanList = weeklyPlan.map(p => {
            if (p.id === todaysPlan.id) return { ...p, completed: true };
            return p;
        });
        await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'plans', 'current'), { workouts: updatedPlanList });
    }
    setActiveWorkout(null);
    setActiveTab('history');
    setIsResting(false);
  };

  const updateFitnessLevel = async (level: FitnessLevel) => {
    if (!user) return;
    setFitnessLevel(level);
    await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'profile'), { level }, { merge: true });
  };

  const cancelWorkout = () => {
    setConfirmModalState({
      isOpen: true, title: "Training abbrechen", message: "Bist du sicher? Dein Fortschritt geht verloren.", isDestructive: true, confirmText: "Ja, abbrechen",
      onConfirm: () => { setActiveWorkout(null); setActiveTab('dashboard'); setIsResting(false); setConfirmModalState({ isOpen: false, title: '', message: '', onConfirm: () => {} }); }
    });
  };

  const deleteWorkout = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmModalState({
      isOpen: true, title: "Workout löschen", message: "Dieser Eintrag wird endgültig entfernt.", isDestructive: true, confirmText: "Löschen",
      onConfirm: async () => {
        if (user) await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'history', id));
        setHistory(prev => prev.filter(w => w.id !== id));
        setConfirmModalState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      }
    });
  };
  
  const deleteBodyStat = async (id: string) => {
    setConfirmModalState({
      isOpen: true, title: "Eintrag löschen", message: "Weg damit?", isDestructive: true, confirmText: "Löschen",
      onConfirm: async () => {
        if (user) await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'stats', id));
        setConfirmModalState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      }
    });
  };

  const resumeWorkout = async (workout: WorkoutSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmModalState({
      isOpen: true, title: "Training fortsetzen", message: "Möchtest du dieses Training wirklich wieder aufnehmen?", confirmText: "Wiederaufnehmen",
      onConfirm: async () => {
        if (user) await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'history', workout.id));
        const todayStr = getTodayDateString();
        if (workout.date.startsWith(todayStr)) {
            const todaysPlan = weeklyPlan.find(p => p.date === todayStr);
            if (todaysPlan && todaysPlan.completed) {
                const updatedPlanList = weeklyPlan.map(p => {
                    if (p.id === todaysPlan.id) return { ...p, completed: false };
                    return p;
                });
                setWeeklyPlan(updatedPlanList);
                if (user) await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'plans', 'current'), { workouts: updatedPlanList });
            }
        }
        setActiveWorkout(workout);
        setElapsedTime(workout.duration * 60);
        setActiveTab('workout');
        setConfirmModalState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      }
    });
  };

  const generateWeeklyPlan = async (weeks: number) => {
    if (!user) return;
    let exercisesCount = 5; let setsCount = 3; let repsCount = 12;
    if (fitnessLevel === 'Beginner') { exercisesCount = 4; setsCount = 2; repsCount = 15; } 
    else if (fitnessLevel === 'Expert') { exercisesCount = 6; setsCount = 4; repsCount = 8; }
    
    const latestStat = bodyStats.length > 0 ? bodyStats[0] : null;
    let focus: 'balanced' | 'cardio_heavy' | 'strength_heavy' = 'balanced';
    if (latestStat) {
      if (latestStat.bodyFat && latestStat.bodyFat > 25) focus = 'cardio_heavy';
      else if (latestStat.muscleMass && latestStat.muscleMass > 40) focus = 'strength_heavy';
    }

    const newPlan: PlannedWorkout[] = [];
    const today = new Date();
    
    const getWorkout = (type: 'Strength' | 'Cardio', subType: 'upper' | 'lower' | 'full' | 'cardio'): WorkoutSession => {
      const available = exerciseLibrary.filter(ex => userEquipment.includes(ex.equipment));
      let candidates: ExerciseTemplate[] = [];
      if (subType === 'upper') candidates = available.filter(ex => ['BRUST', 'RÜCKEN', 'SCHULTERN', 'ARME'].includes(ex.muscleGroup));
      else if (subType === 'lower') candidates = available.filter(ex => ['BEINE', 'RUMPF'].includes(ex.muscleGroup));
      else if (subType === 'cardio') candidates = available.filter(ex => ['AUSDAUER'].includes(ex.muscleGroup));
      else candidates = available; 
      const shuffled = [...candidates].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, exercisesCount); 
      const generatedSets = Array(setsCount).fill(null).map(() => ({ id: generateId(), reps: repsCount, weight: 0, completed: false }));
      return {
        id: generateId(), date: '', name: `AI ${subType === 'upper' ? 'Oberkörper' : subType === 'lower' ? 'Unterkörper' : subType === 'cardio' ? 'Cardio Burn' : 'Ganzkörper'}`,
        duration: 0, exercises: selected.map(template => ({
          id: generateId(), name: template.name, muscleGroup: template.muscleGroup, equipment: template.equipment,
          imageUrl: getExerciseImage(template), instructions: template.instructions || null, videoUrl: template.videoUrl || null,
          secondaryMuscles: template.secondaryMuscles || [], sets: JSON.parse(JSON.stringify(generatedSets)) 
        })), totalVolume: 0
      };
    };

    for (let i = 0; i < weeks * 7; i++) {
      const currentDate = new Date(today);
      currentDate.setDate(today.getDate() + i);
      const dateStr = currentDate.toISOString().split('T')[0];
      const dayOfWeek = currentDate.getDay(); 
      let workoutType: 'Strength' | 'Cardio' | 'Rest' = 'Rest';
      let template: WorkoutSession | null = null;
      if (focus === 'strength_heavy' || focus === 'balanced') {
        if (dayOfWeek === 1) { workoutType = 'Strength'; template = getWorkout('Strength', 'upper'); } 
        else if (dayOfWeek === 2) { workoutType = 'Strength'; template = getWorkout('Strength', 'lower'); } 
        else if (dayOfWeek === 4) { workoutType = 'Strength'; template = getWorkout('Strength', 'upper'); } 
        else if (dayOfWeek === 5) { workoutType = 'Strength'; template = getWorkout('Strength', 'lower'); } 
        else if (dayOfWeek === 3 && focus === 'cardio_heavy') { workoutType = 'Cardio'; template = getWorkout('Cardio', 'cardio'); } 
      } else { 
        if (dayOfWeek === 1 || dayOfWeek === 4) { workoutType = 'Strength'; template = getWorkout('Strength', 'full'); }
        else if (dayOfWeek === 2 || dayOfWeek === 5) { workoutType = 'Cardio'; template = getWorkout('Cardio', 'cardio'); }
      }
      if (fitnessLevel === 'Beginner' && workoutType !== 'Rest') { if (dayOfWeek === 3) { workoutType = 'Rest'; template = null; } }
      newPlan.push({
        id: generateId(), date: dateStr, dayName: currentDate.toLocaleDateString('de-DE', { weekday: 'long' }),
        workoutTemplate: template, completed: false, type: workoutType
      });
    }
    await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'plans', 'current'), { workouts: newPlan });
  };

  // --- Effects ---
  useEffect(() => {
    const initAuth = async () => {
      if (isConfigured) { await signInAnonymously(auth); return; }
      if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) { await signInWithCustomToken(auth, __initial_auth_token); } 
      else { await signInAnonymously(auth); }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => { setUser(currentUser); });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handler = (e: any) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsubHistory = onSnapshot(getUserCollection(user.uid, 'history'), (snapshot) => {
      const data = snapshot.docs.map(doc => doc.data() as WorkoutSession);
      setHistory(data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    });
    const unsubSettings = onSnapshot(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'profile'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.equipment) setUserEquipment(data.equipment);
        if (data.level) setFitnessLevel(data.level);
        if (data.name) setUserName(data.name);
        if (data.photo) setUserPhoto(data.photo);
        if (data.height) setUserHeight(data.height);
        if (data.birthDate) setUserBirthDate(data.birthDate);
      }
    });
    const unsubStats = onSnapshot(getUserCollection(user.uid, 'stats'), (snapshot) => {
      const data = snapshot.docs.map(doc => doc.data() as BodyStat);
      setBodyStats(data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    });
    const unsubPlan = onSnapshot(doc(db, 'artifacts', appId, 'users', user.uid, 'plans', 'current'), (docSnap) => {
      if (docSnap.exists()) { setWeeklyPlan(docSnap.data().workouts || []); }
    });
    const unsubSupps = onSnapshot(getUserCollection(user.uid, 'supplements'), (snapshot) => {
      const data = snapshot.docs.map(doc => doc.data() as Supplement);
      setSupplements(data.sort((a, b) => a.time.localeCompare(b.time)));
    });
    const unsubExercises = onSnapshot(getUserCollection(user.uid, 'custom_exercises'), (snapshot) => {
      const data = snapshot.docs.map(doc => doc.data() as ExerciseTemplate);
      const customMap = new Map(data.map(ex => [ex.id, ex]));
      const merged = INITIAL_EXERCISES.map(ex => customMap.get(ex.id) || ex);
      const newOnly = data.filter(ce => !INITIAL_EXERCISES.find(ie => ie.id === ce.id));
      setExerciseLibrary([...merged, ...newOnly]);
    });
    return () => { unsubHistory(); unsubSettings(); unsubStats(); unsubPlan(); unsubSupps(); unsubExercises(); };
  }, [user]);

  useEffect(() => {
    const handler = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handler);
    window.addEventListener('offline', handler);
    fetchDailyMotivation();
    return () => { window.removeEventListener('online', handler); window.removeEventListener('offline', handler); };
  }, []);

  useEffect(() => {
    if (activeWorkout && !timerInterval) {
      const interval = setInterval(() => setElapsedTime(prev => prev + 1), 1000);
      setTimerInterval(interval);
    } else if (!activeWorkout && timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
      setElapsedTime(0);
    }
    return () => { if (timerInterval) clearInterval(timerInterval); };
  }, [activeWorkout]);

  // --- Render Functions (now inside App and safe) ---

  const renderHistory = () => (
    <div className="space-y-4 pb-20">
      <h2 className="text-3xl font-black text-white italic tracking-tighter mb-8 pt-4">History</h2>
      <VolumeChart data={history} />
      <MuscleHeatmap history={history} />
      {history.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-neutral-600">
          <Calendar size={64} strokeWidth={1} className="mb-4 opacity-20" />
          <p className="text-sm font-mono uppercase tracking-widest">No logs found</p>
        </div>
      ) : (
        history.map((workout) => (
          <div key={workout.id} className="bg-neutral-900 rounded-none border-l-4 border-neutral-800 hover:border-lime-500 p-5 transition-all group relative">
            <div className="absolute top-4 right-4 flex gap-2 z-10">
              <button onClick={(e) => resumeWorkout(workout, e)} className="text-neutral-700 hover:text-lime-400 p-2 transition-colors"><RotateCcw size={16} /></button>
              <button onClick={(e) => deleteWorkout(workout.id, e)} className="text-neutral-700 hover:text-red-500 p-2 transition-colors"><Trash2 size={16} /></button>
            </div>
            <div className="flex justify-between items-start mb-4 border-b border-neutral-800 pb-4 pr-8">
              <div>
                <h3 className="font-bold text-lg text-white uppercase italic">{workout.name}</h3>
                <p className="text-neutral-500 text-xs font-mono uppercase mt-1 flex items-center gap-2"><span className="text-lime-400">{formatDate(workout.date)}</span><span>|</span><span>{workout.duration} MIN</span></p>
              </div>
              <div className="text-white text-xl font-black italic">{(workout.totalVolume / 1000).toFixed(1)}<span className="text-sm text-lime-400 not-italic ml-0.5">t</span></div>
            </div>
            <div className="space-y-2">
              {workout.exercises.map((ex, idx) => (
                <div key={idx} className="flex justify-between text-xs items-center">
                  <span className="text-neutral-400 font-medium uppercase">{ex.sets.length} x <span className="text-neutral-200">{ex.name}</span></span>
                  <span className="text-neutral-500 font-mono">{Math.max(...ex.sets.map(s => s.weight))} kg</span>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderWorkoutLogger = () => {
    if (!activeWorkout) return null;
    const formatTime = (seconds: number) => { const mins = Math.floor(seconds / 60); const secs = seconds % 60; return `${mins}:${secs < 10 ? '0' : ''}${secs}`; };
    const availableExercises = exerciseLibrary.filter(ex => {
      const matchesEquipment = !showOnlyAvailable || userEquipment.includes(ex.equipment);
      const matchesSearch = searchTerm.length < 2 || ex.name.toLowerCase().includes(searchTerm.toLowerCase()) || ex.muscleGroup.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesEquipment && matchesSearch;
    });
    const groupedExercises: Record<string, ExerciseTemplate[]> = {};
    availableExercises.forEach(ex => { if (!groupedExercises[ex.muscleGroup]) groupedExercises[ex.muscleGroup] = []; groupedExercises[ex.muscleGroup].push(ex); });

    return (
      <div className="pb-24 h-full flex flex-col bg-black">
        <div className="sticky top-0 bg-black/90 backdrop-blur-md z-20 pb-4 border-b border-neutral-800 pt-2 px-1">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2 text-lime-400 font-mono font-bold bg-lime-400/10 px-4 py-1.5 rounded text-sm border border-lime-400/20"><Clock size={14} />{formatTime(elapsedTime)}</div>
            <button onClick={finishWorkout} className="bg-lime-500 hover:bg-lime-400 text-black px-6 py-1.5 rounded text-sm font-black uppercase tracking-wider shadow-[0_0_15px_rgba(132,204,22,0.4)] transition-all">Finish</button>
          </div>
          <input value={activeWorkout.name} onChange={(e) => setActiveWorkout({...activeWorkout, name: e.target.value})} className="text-xl font-black italic uppercase text-white w-full bg-transparent outline-none placeholder-neutral-600 tracking-wide" placeholder="SESSION NAME" />
        </div>
        
        {/* ALPHA PROGRESSION STYLE LOGGER */}
        <div className="flex-1 overflow-y-auto space-y-6 pt-6">
          {activeWorkout.exercises.map((exercise) => (
            <div key={exercise.id} className="bg-neutral-900 rounded-2xl overflow-hidden border border-neutral-800">
              <div className="bg-black/40 px-4 py-3 flex justify-between items-center border-b border-neutral-800">
                <div className="flex items-center gap-3">
                  {exercise.imageUrl && <img src={exercise.imageUrl} alt={exercise.name} className="w-10 h-10 rounded-full object-cover border border-neutral-700" />}
                  <div>
                    <div className="font-bold text-blue-400 text-lg tracking-wide flex items-center gap-2">{exercise.name}<button onClick={() => setShowInfoModal(exercise as unknown as ExerciseTemplate)} className="text-neutral-500 hover:text-lime-400"><MoreHorizontal size={18} /></button></div>
                    <div className="flex items-center gap-2 text-[10px] text-neutral-500 font-mono uppercase tracking-widest">
                       <span>{EQUIPMENT_LIST.find(e => e.id === exercise.equipment)?.label}</span>
                       <span className="text-neutral-700">•</span>
                       <span>{exercise.sets.length} Sätze</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-2">
                {/* Header Row */}
                <div className="grid grid-cols-10 gap-2 mb-2 px-2 text-[10px] font-bold text-neutral-500 uppercase text-center tracking-widest items-center">
                    <div className="col-span-1">#</div>
                    <div className="col-span-3">KG</div>
                    <div className="col-span-3">WDH</div>
                    <div className="col-span-3 flex justify-center"><CheckCircle2 size={12} /></div>
                </div>
                
                {exercise.sets.map((set, index) => (
                  <div key={set.id} className={`grid grid-cols-10 gap-2 mb-2 items-center transition-all ${set.completed ? 'opacity-50' : ''}`}>
                    <div className="col-span-1 text-center font-bold text-neutral-400 font-mono text-sm bg-neutral-800/50 rounded py-2">{index + 1}</div>
                    <div className="col-span-3"><input type="number" value={set.weight} onChange={(e) => updateSet(exercise.id, set.id, 'weight', parseFloat(e.target.value))} className="w-full text-center bg-neutral-800 text-white rounded-lg border border-neutral-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 py-2.5 font-bold font-mono outline-none text-sm transition-colors" placeholder="0" /></div>
                    <div className="col-span-3"><input type="number" value={set.reps} onChange={(e) => updateSet(exercise.id, set.id, 'reps', parseFloat(e.target.value))} className="w-full text-center bg-neutral-800 text-white rounded-lg border border-neutral-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 py-2.5 font-bold font-mono outline-none text-sm transition-colors" placeholder="0" /></div>
                    <div className="col-span-3 flex gap-1">
                        <button onClick={() => updateSet(exercise.id, set.id, 'completed', !set.completed)} className={`flex-1 py-2 rounded-lg flex items-center justify-center transition-all ${set.completed ? 'bg-green-500 text-black shadow-[0_0_10px_rgba(34,197,94,0.4)]' : 'bg-neutral-800 text-neutral-500 hover:bg-neutral-700'}`}>
                            {set.completed ? <CheckCircle2 size={18} strokeWidth={3} /> : <CheckCircle2 size={18} />}
                        </button>
                        <button onClick={() => removeSet(exercise.id, set.id)} className="w-8 flex items-center justify-center bg-neutral-800 rounded-lg text-neutral-600 hover:text-red-500 hover:bg-red-900/20 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => addSet(exercise.id)} className="w-full mt-2 py-3 text-xs text-blue-400 font-bold uppercase tracking-widest bg-blue-900/10 hover:bg-blue-900/20 rounded-lg transition-all flex items-center justify-center gap-2 border border-blue-900/30 hover:border-blue-500/50"><Plus size={14} /> Satz hinzufügen</button>
              </div>
            </div>
          ))}
          <div className="pt-6 pb-8">
            <div className="flex flex-col gap-4 mb-4 px-1">
               <div className="flex justify-between items-end"><h3 className="text-xs font-bold text-neutral-500 uppercase tracking-widest">Übung hinzufügen</h3><button onClick={() => setShowOnlyAvailable(!showOnlyAvailable)} className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 px-2 py-1 rounded border transition-colors ${showOnlyAvailable ? 'text-lime-400 border-lime-500/50 bg-lime-900/10' : 'text-neutral-500 border-neutral-800'}`}><Filter size={10} />{showOnlyAvailable ? 'My Equipment' : 'Alle'}</button></div>
               <div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500" size={16} /><input type="text" placeholder="Suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-lg py-3 pl-10 pr-4 text-white text-sm focus:border-lime-500 focus:outline-none placeholder-neutral-600" /></div>
            </div>
            {availableExercises.length === 0 ? (<div className="text-center py-6 border border-dashed border-neutral-800 rounded text-neutral-500 text-xs">Keine Übungen gefunden.</div>) : (
              <div className="space-y-6">{Object.entries(groupedExercises).map(([muscle, exercises]) => (
                  <div key={muscle}><h4 className="text-[10px] font-bold text-lime-500/80 uppercase tracking-widest mb-2 pl-1 border-b border-neutral-800 pb-1 w-full max-w-[100px]">{muscle}</h4>
                    <div className="grid grid-cols-2 gap-2">{exercises.map(ex => (
                        <button key={ex.id} onClick={() => addExerciseToWorkout(ex)} className="p-3 text-left bg-neutral-900 border border-neutral-800 rounded hover:border-lime-500 hover:bg-neutral-800 transition-all group relative overflow-hidden flex flex-col items-start gap-2 h-full">
                           <div className="flex justify-between w-full">
                              <img src={getExerciseImage(ex)} alt={ex.muscleGroup} className="w-8 h-8 rounded object-cover bg-neutral-800" />
                              <div className={`w-1.5 h-1.5 rounded-full ${userEquipment.includes(ex.equipment) ? 'bg-lime-500' : 'bg-neutral-700'}`}></div>
                           </div>
                          <div><div className="font-bold text-gray-200 group-hover:text-lime-400 text-sm uppercase truncate w-full">{ex.name}</div><div className="text-[10px] text-neutral-500 font-mono mt-1"><span>{ex.muscleGroup}</span></div></div>
                        </button>
                      ))}</div>
                  </div>
                ))}</div>
            )}
          </div>
          <button onClick={cancelWorkout} className="w-full text-red-500/50 hover:text-red-500 text-xs font-bold uppercase tracking-widest py-6 mb-8 transition-colors">Training Abbrechen</button>
        </div>
      </div>
    );
  };

  const renderDashboard = () => {
    const totalVolume = history.reduce((acc, curr) => acc + curr.totalVolume, 0);
    const thisWeekWorkouts = history.filter(w => {
      const diffDays = Math.ceil(Math.abs(new Date().getTime() - new Date(w.date).getTime()) / (1000 * 60 * 60 * 24)); 
      return diffDays <= 7;
    }).length;
    const todayStr = getTodayDateString();
    const todaysPlan = weeklyPlan.find(p => p.date === todayStr);

    const now = new Date();
    const nowTime = now.getHours() * 60 + now.getMinutes();
    const dueSupplements = supplements.filter(s => {
      const [h, m] = s.time.split(':').map(Number);
      const suppTime = h * 60 + m;
      const minutesSinceLastTaken = s.lastTaken ? (now.getTime() - new Date(s.lastTaken).getTime()) / (1000 * 60) : Infinity;
      return Math.abs(suppTime - nowTime) <= 60 && minutesSinceLastTaken > (8 * 60);
    });

    const nextWorkoutTime = weeklyPlan.filter(p => !p.completed && p.workoutTemplate && new Date(p.date) >= now).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
    let nextWorkoutReminder = null;
    if (nextWorkoutTime) {
      const diffHours = Math.round((new Date(nextWorkoutTime.date).getTime() - now.getTime()) / (1000 * 60 * 60));
      if (diffHours >= 0 && diffHours <= 24) { nextWorkoutReminder = `Dein nächstes Training ("${nextWorkoutTime.workoutTemplate?.name}") startet in ca. ${diffHours} Stunden!`; }
    }

    return (
      <div className="space-y-6 pb-20 animate-in fade-in duration-500">
        {dueSupplements.length > 0 && (
            <div className="bg-purple-900/20 border border-purple-500/50 rounded-xl p-4 relative shadow-[0_0_15px_rgba(168,85,247,0.1)]">
                <h3 className="text-purple-400 font-bold uppercase text-xs tracking-widest mb-2 flex items-center gap-2"><Pill size={14} /> Einnahme fällig!</h3>
                {dueSupplements.map(supp => (
                    <div key={supp.id} className="flex justify-between items-center bg-purple-900/30 p-2 rounded-lg mb-1">
                        <span className="text-white text-sm font-medium">{supp.name} ({supp.dosage})</span>
                        <button onClick={() => logSupplementTaken(supp)} className="bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded transition-colors">Genommen</button>
                    </div>
                ))}
            </div>
        )}
        {nextWorkoutReminder && (
            <div className="bg-blue-900/20 border border-blue-500/50 rounded-xl p-4 relative shadow-[0_0_15px_rgba(59,130,246,0.1)]">
                <h3 className="text-blue-400 font-bold uppercase text-xs tracking-widest mb-2 flex items-center gap-2"><Timer size={14} /> TRAINING BEVORSTEHEND</h3>
                <p className="text-neutral-300 text-sm">{nextWorkoutReminder}</p>
            </div>
        )}
        
        {todaysPlan && todaysPlan.type !== 'Rest' && !todaysPlan.completed ? (
           <div className="bg-lime-900/10 border border-lime-500/50 rounded-xl p-5 relative overflow-hidden shadow-[0_0_30px_rgba(163,230,53,0.1)]">
              <div className="absolute right-0 top-0 p-3 opacity-10"><CalendarDays size={64} className="text-lime-400" /></div>
              <div className="relative z-10">
                 <h2 className="text-lime-400 font-bold uppercase text-xs tracking-widest mb-1 flex items-center gap-2"><BrainCircuit size={14} /> Dein Plan für Heute</h2>
                 <h3 className="text-2xl font-black text-white italic uppercase mb-4">{todaysPlan.workoutTemplate?.name}</h3>
                 <div className="flex gap-2">
                   <button onClick={() => startPlannedWorkout(todaysPlan)} className="flex-1 bg-lime-400 text-black py-3 rounded-lg font-black uppercase tracking-wider hover:bg-lime-300 transition-all flex items-center justify-center gap-2"><Play size={18} fill="black" /> Starten</button>
                   <button onClick={startWorkout} className="px-4 bg-black border border-neutral-700 text-neutral-400 rounded-lg font-bold uppercase tracking-wider hover:text-white transition-all" title="Anderes Workout wählen"><SkipForward size={18} /></button>
                 </div>
              </div>
           </div>
        ) : todaysPlan?.completed ? (
           <div className="bg-neutral-900/50 border border-green-900/50 rounded-xl p-4 flex items-center gap-4">
              <div className="bg-green-900/20 p-3 rounded-full text-green-500"><CheckCircle2 size={24} /></div><div><h3 className="text-white font-bold uppercase text-sm">Tagesziel erreicht!</h3><p className="text-neutral-500 text-xs">Starke Leistung heute.</p></div>
           </div>
        ) : (
           <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 flex flex-col items-center text-center">
              <div className="bg-neutral-800 p-3 rounded-full text-neutral-500 mb-3"><RotateCcw size={24} /></div>
              <h3 className="text-white font-bold uppercase text-sm mb-1">Heute ist Ruhetag</h3>
              <p className="text-neutral-500 text-xs mb-4 max-w-[80%]">Oder noch kein Plan erstellt? Gehe zum Planer, um deine Woche zu strukturieren.</p>
              <button onClick={() => setActiveTab('plan')} className="text-lime-400 text-xs font-bold uppercase border-b border-lime-400 pb-0.5 hover:text-lime-300">Zum Planer</button>
           </div>
        )}

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="bg-neutral-900 rounded-xl p-5 border border-neutral-800 relative overflow-hidden group hover:border-lime-500/50 transition-all"><div className="flex items-center gap-2 mb-3"><Activity size={16} className="text-lime-400" /><span className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Woche</span></div><div className="text-4xl font-black text-white italic">{thisWeekWorkouts}</div><div className="text-[10px] text-neutral-500 mt-1 uppercase font-medium">Einheiten</div></div>
          <div className="bg-neutral-900 rounded-xl p-5 border border-neutral-800 relative overflow-hidden group hover:border-lime-500/50 transition-all"><div className="flex items-center gap-2 mb-3"><Dumbbell size={16} className="text-lime-400" /><span className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Volumen</span></div><div className="text-3xl font-black text-white italic">{(totalVolume / 1000).toFixed(1)}<span className="text-lg text-neutral-500 ml-1 not-italic">t</span></div><div className="text-[10px] text-neutral-500 mt-1 uppercase font-medium">Gesamtlast</div></div>
        </div>

        {!todaysPlan || todaysPlan.type === 'Rest' || todaysPlan.completed ? (
          <div className="bg-gradient-to-br from-neutral-800 via-neutral-900 to-black rounded-xl p-6 text-white shadow-2xl border border-lime-500/20 relative overflow-hidden mt-6">
            <div className="absolute left-0 bottom-0 h-full w-1 bg-lime-400"></div>
            <div className="flex justify-between items-start mb-4 relative z-10"><div><h2 className="text-xl font-black uppercase italic tracking-wide">Freies Training</h2><p className="text-neutral-400 text-xs mt-1 max-w-[90%]">Kein Plan? Kein Problem. Logge manuell.</p></div><Zap className="text-lime-400 animate-pulse" /></div>
            <button onClick={startWorkout} className="w-full bg-lime-400 text-black py-4 rounded-lg font-black uppercase tracking-wider hover:bg-lime-300 transition-all shadow-[0_0_20px_rgba(163,230,53,0.3)] flex items-center justify-center gap-3 active:scale-[0.98]"><Plus size={22} strokeWidth={3} /> Training Starten</button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderPlan = () => (
    <div className="pb-24 h-full flex flex-col">
      <div className="p-5 flex-1 overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
             <h2 className="text-3xl font-black text-white italic tracking-tighter uppercase">Dein <span className="text-lime-400">Plan</span></h2>
             <button onClick={() => generateWeeklyPlan(1)} className="text-[10px] bg-neutral-800 hover:bg-neutral-700 px-3 py-1.5 rounded-lg border border-neutral-700 font-bold uppercase tracking-widest text-neutral-400 hover:text-white transition-colors">Neu Generieren</button>
        </div>
        
        {weeklyPlan.length === 0 ? (
           <div className="text-center py-10 opacity-50"><CalendarDays size={48} className="mx-auto mb-4 text-neutral-600" /><p className="text-sm font-mono uppercase tracking-widest">Noch kein Plan erstellt.</p></div>
        ) : (
          <div className="space-y-4">
            {weeklyPlan.map((day) => {
               const isToday = day.date === getTodayDateString();
               const isRest = day.type === 'Rest';
               const dateDisplay = new Date(day.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
               
               return (
                 <div key={day.id} className={`bg-neutral-900 rounded-2xl overflow-hidden border ${isToday ? 'border-lime-500 shadow-[0_0_15px_rgba(163,230,53,0.15)]' : 'border-neutral-800'} transition-all`}>
                    <div className="p-4 border-b border-neutral-800/50 flex justify-between items-center bg-black/20">
                        <div>
                            <div className="text-lg font-black text-white uppercase italic tracking-wide">{day.dayName}</div>
                            <div className="text-[10px] text-neutral-500 font-mono uppercase tracking-widest mt-0.5">{day.type} · {dateDisplay}</div>
                        </div>
                        {isToday && <div className="bg-lime-500 text-black text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-widest">Heute</div>}
                    </div>
                    
                    {!isRest && day.workoutTemplate && (
                        <div className="p-2">
                             <div className="flex flex-col gap-1">
                                {day.workoutTemplate.exercises.map((ex, i) => (
                                    <div key={i} className="flex items-center gap-3 p-2 rounded-xl hover:bg-neutral-800/40 transition-colors group cursor-pointer" onClick={() => setShowInfoModal(ex)}>
                                        <div className="w-10 h-10 rounded-full bg-neutral-800 overflow-hidden border border-neutral-700 shrink-0">
                                            <img src={getExerciseImage(ex)} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold text-gray-200 group-hover:text-white truncate">{ex.name}</div>
                                            <div className="text-[10px] text-neutral-500 flex items-center gap-1.5 font-mono">
                                                <span className="uppercase tracking-wide">{EQUIPMENT_LIST.find(e => e.id === ex.equipment)?.label}</span>
                                                <span className="w-0.5 h-0.5 rounded-full bg-neutral-600"></span>
                                                <span>{ex.sets.length} x 12</span>
                                            </div>
                                        </div>
                                        <div className="text-neutral-600 group-hover:text-lime-400 transition-colors"><ChevronRight size={18} /></div>
                                    </div>
                                ))}
                             </div>
                             {!day.completed && (
                                 <button onClick={() => { setActiveWorkout({ ...day.workoutTemplate!, id: generateId(), date: new Date().toISOString(), name: day.workoutTemplate!.name }); setActiveTab('workout'); }} className="w-[calc(100%-1rem)] mx-2 mb-2 mt-2 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-xl uppercase tracking-wide flex items-center justify-center gap-2 transition-all active:scale-[0.98]"><Play size={16} fill="white" /> Workout Starten</button>
                             )}
                             {day.completed && <div className="p-3 text-center text-green-500 font-bold uppercase text-xs tracking-widest flex items-center justify-center gap-2"><CheckCircle2 size={14} /> Abgeschlossen</div>}
                        </div>
                    )}
                    {isRest && <div className="p-8 text-center"><div className="w-12 h-12 bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-3 text-neutral-500"><BatteryCharging size={24} /></div><div className="text-neutral-500 text-xs uppercase tracking-widest font-bold">Erholung ist wichtig</div><div className="text-neutral-600 text-[10px] mt-1">Gönn deinen Muskeln eine Pause.</div></div>}
                 </div>
               )
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="pb-24 h-full flex flex-col">
       <div className="p-5 flex-1 overflow-y-auto">
         {profileSubTab === 'stats' && (
           <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
             <div className="bg-neutral-900 rounded-xl p-5 border border-neutral-800 relative">
               <h3 className="text-white font-bold uppercase tracking-wider mb-4 flex items-center gap-2 text-sm">
                 <User size={16} className="text-lime-400" /> Basisdaten
                 <button onClick={() => setShowProfileEdit(true)} className="ml-auto bg-neutral-800 p-1.5 rounded-full hover:bg-neutral-700 transition-colors">
                    {isEditingBasicStats ? <CheckCircle2 size={12} className="text-lime-400" /> : <Edit3 size={12} className="text-neutral-400" />}
                 </button>
               </h3>
               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="text-[9px] text-neutral-500 font-bold uppercase block mb-1">Größe</label>
                   {isEditingBasicStats ? (
                     <div className="relative">
                       <Ruler size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-600" />
                       <input type="number" value={userHeight} onChange={(e) => setUserHeight(e.target.value)} onBlur={updateBasicStats} className="w-full bg-black border border-neutral-700 rounded-lg py-2 pl-8 pr-2 text-white text-sm focus:border-lime-500 outline-none" placeholder="180" />
                     </div>
                   ) : (<div className="text-lg font-bold text-white font-mono">{userHeight ? `${userHeight} cm` : '-'}</div>)}
                 </div>
                 <div>
                   <label className="text-[9px] text-neutral-500 font-bold uppercase block mb-1">Geburtsdatum</label>
                   {isEditingBasicStats ? (
                     <div className="relative">
                       <Calendar size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-600" />
                       <input type="date" value={userBirthDate} onChange={(e) => setUserBirthDate(e.target.value)} onBlur={updateBasicStats} className="w-full bg-black border border-neutral-700 rounded-lg py-2 pl-8 pr-2 text-white text-sm focus:border-lime-500 outline-none" />
                     </div>
                   ) : (<div className="text-lg font-bold text-white font-mono">{userBirthDate ? formatDate(userBirthDate) : '-'}</div>)}
                 </div>
               </div>
               {userHeight && bodyStats.length > 0 && (
                 <div className="mt-4 pt-4 border-t border-neutral-800 flex items-center justify-between">
                   <div className="text-xs text-neutral-500 font-mono uppercase">BMI Index</div>
                   <div className="flex items-center gap-2"><Calculator size={14} className="text-lime-400" /><span className="text-white font-bold text-lg">{calculateBMI(bodyStats[0].weight, parseFloat(userHeight))}</span></div>
                 </div>
               )}
             </div>
             <WeightChart data={bodyStats} />
             <div className="bg-neutral-900 rounded-xl p-5 border border-neutral-800">
               <h3 className="text-white font-bold uppercase tracking-wider mb-4 flex items-center gap-2 text-sm"><Target size={16} className="text-lime-400" /> Fitness Level</h3>
               <div className="flex gap-2">
                 {(['Beginner', 'Intermediate', 'Expert'] as FitnessLevel[]).map(lvl => (
                   <button key={lvl} onClick={() => updateFitnessLevel(lvl)} className={`flex-1 py-3 rounded-lg text-xs font-bold uppercase border transition-all ${fitnessLevel === lvl ? 'bg-lime-900/20 border-lime-500 text-lime-400 shadow-[0_0_10px_rgba(163,230,53,0.1)]' : 'bg-black border-neutral-700 text-neutral-500 hover:border-neutral-500'}`}>{lvl === 'Beginner' ? 'Anfänger' : lvl === 'Intermediate' ? 'Fortgeschr.' : 'Experte'}</button>
                 ))}
               </div>
               <p className="text-[10px] text-neutral-600 mt-2 italic text-center">{fitnessLevel === 'Beginner' ? 'Fokus: Technik & Ausdauer (15-20 Wdh)' : fitnessLevel === 'Intermediate' ? 'Fokus: Muskelaufbau (8-12 Wdh)' : 'Fokus: Kraft & Volumen (5-8 Wdh)'}</p>
             </div>
             <div className="bg-neutral-900 rounded-xl p-5 border border-neutral-800">
               <h3 className="text-white font-bold uppercase tracking-wider mb-4 flex items-center gap-2 text-sm"><Plus size={16} className="text-lime-400" /> Update Stats</h3>
               <div className="grid grid-cols-3 gap-3 mb-4">
                 <div><label className="text-[9px] text-neutral-500 font-bold uppercase block mb-1">Gewicht</label><div className="relative"><Scale size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-600" /><input type="number" value={newWeight} onChange={e => setNewWeight(e.target.value)} className="w-full bg-black border border-neutral-800 rounded-lg py-2 pl-8 pr-2 text-white text-sm focus:border-lime-500 outline-none" placeholder="0.0" /></div></div>
                 <div><label className="text-[9px] text-neutral-500 font-bold uppercase block mb-1">KFA (%)</label><div className="relative"><Percent size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-600" /><input type="number" value={newBodyFat} onChange={e => setNewBodyFat(e.target.value)} className="w-full bg-black border border-neutral-800 rounded-lg py-2 pl-8 pr-2 text-white text-sm focus:border-lime-500 outline-none" placeholder="Opt" /></div></div>
                 <div><label className="text-[9px] text-neutral-500 font-bold uppercase block mb-1">Muskel (%)</label><div className="relative"><Dna size={12} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-600" /><input type="number" value={newMuscleMass} onChange={e => setNewMuscleMass(e.target.value)} className="w-full bg-black border border-neutral-800 rounded-lg py-2 pl-8 pr-2 text-white text-sm focus:border-lime-500 outline-none" placeholder="Opt" /></div></div>
               </div>
               <button onClick={addBodyStat} className="w-full bg-lime-900/20 text-lime-400 border border-lime-900/50 py-2 rounded-lg text-xs font-bold uppercase hover:bg-lime-900/40 transition-colors">Log Entry</button>
             </div>
             <div className="space-y-2">
               <h3 className="text-neutral-500 text-xs font-bold uppercase tracking-widest pl-1">Verlauf</h3>
               {bodyStats.length === 0 ? (<div className="text-center py-8 text-neutral-600 text-xs italic">Keine Daten vorhanden.</div>) : (
                 bodyStats.map(stat => (
                   <div key={stat.id} className="bg-neutral-900 border border-neutral-800 p-3 rounded-lg flex justify-between items-center group">
                     <div><div className="text-white font-bold font-mono">{stat.weight} kg</div><div className="text-[10px] text-neutral-500">{formatDate(stat.date)}</div></div>
                     <div className="flex gap-4 text-xs font-mono text-neutral-400">{stat.bodyFat !== null ? <span><span className="text-neutral-600">KFA:</span> {stat.bodyFat}%</span> : null}{stat.muscleMass !== null ? <span><span className="text-neutral-600">MUS:</span> {stat.muscleMass}%</span> : null}</div>
                     <button onClick={() => deleteBodyStat(stat.id)} className="text-neutral-700 hover:text-red-500"><Trash2 size={14} /></button>
                   </div>
                 ))
               )}
             </div>
           </div>
         )}
         {profileSubTab === 'equipment' && (
           <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
              <p className="text-neutral-500 text-xs mb-4">Wähle, was du zuhause hast. Der AI Planer nutzt diese Infos.</p>
              {Array.from(new Set(EQUIPMENT_LIST.map(e => e.category))).map((category) => {
                const isExpanded = expandedCategories.includes(category);
                const categoryEquipment = EQUIPMENT_LIST.filter(e => e.category === category);
                const activeCount = categoryEquipment.filter(e => userEquipment.includes(e.id)).length;
                return (
                  <div key={category} className="bg-neutral-900 rounded-xl border border-neutral-800 overflow-hidden">
                    <button onClick={() => setExpandedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category])} className="w-full flex justify-between items-center p-4 bg-neutral-900 hover:bg-neutral-800">
                      <div className="flex items-center gap-3"><span className="font-bold uppercase tracking-wider text-xs text-white">{category}</span>{!isExpanded && activeCount > 0 && <span className="text-[9px] font-bold bg-lime-900/30 text-lime-400 px-2 py-0.5 rounded border border-lime-500/20">{activeCount} Aktiv</span>}</div>
                      {isExpanded ? <ChevronUp size={16} className="text-neutral-500" /> : <ChevronDown size={16} className="text-neutral-500" />}
                    </button>
                    {isExpanded && (
                      <div className="p-3 pt-0 grid grid-cols-1 gap-2 border-t border-neutral-800/50 mt-1">
                        <div className="h-1"></div>
                        {categoryEquipment.map((eq) => {
                          const isActive = userEquipment.includes(eq.id);
                          return (
                            <button key={eq.id} onClick={() => { const newEq = isActive ? userEquipment.filter(e => e !== eq.id) : [...userEquipment, eq.id]; setUserEquipment(newEq); if(user) setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'profile'), { equipment: newEq }, { merge: true }); }} className={`flex justify-between items-center p-3 rounded-lg border transition-all ${isActive ? 'bg-lime-900/10 border-lime-500 text-white' : 'bg-black border-neutral-800 text-neutral-500'}`}>
                              <div className="flex items-center gap-3"><div className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-lime-400 shadow-[0_0_8px_rgba(163,230,53,0.8)]' : 'bg-neutral-800'}`}></div><span className="font-bold uppercase tracking-wide text-xs block text-left">{eq.label}</span></div>
                              {isActive ? <CheckCircle2 size={14} className="text-lime-400" /> : <XCircle size={14} className="text-neutral-700" />}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
           </div>
         )}
         {profileSubTab === 'library' && (
           <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
             <div className="relative mb-4"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500" size={16} /><input type="text" placeholder="Bibliothek durchsuchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full bg-neutral-900 border border-neutral-800 rounded-lg py-3 pl-10 pr-4 text-white text-sm focus:border-lime-500 focus:outline-none placeholder-neutral-600" /></div>
             {Object.entries(exerciseLibrary.reduce((acc, ex) => {
                const matchesSearch = ex.name.toLowerCase().includes(searchTerm.toLowerCase());
                if (!matchesSearch) return acc;
                if (!acc[ex.muscleGroup]) acc[ex.muscleGroup] = [];
                acc[ex.muscleGroup].push(ex);
                return acc;
             }, {} as Record<string, ExerciseTemplate[]>)).map(([muscle, exercises]) => (
                <div key={muscle} className="mb-4">
                  <h4 className="text-[10px] font-bold text-lime-500/80 uppercase tracking-widest mb-2 pl-1 border-b border-neutral-800 pb-1 w-full">{muscle}</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {exercises.map(ex => (
                      <div key={ex.id} className="p-3 bg-neutral-900 border border-neutral-800 rounded flex gap-4 items-center group hover:border-lime-500/30 transition-colors relative" onClick={() => setShowInfoModal(ex)}>
                           <img src={getExerciseImage(ex)} alt={ex.muscleGroup} className="w-12 h-12 rounded bg-neutral-800 object-cover opacity-80" />
                           <div className="flex-1 cursor-pointer"><span className="text-xs font-bold text-gray-300 uppercase block">{ex.name}</span><span className="text-[9px] font-mono text-neutral-500 border border-neutral-800 px-1 rounded inline-block mt-1">{EQUIPMENT_LIST.find(e => e.id === ex.equipment)?.label}</span></div>
                           <button className="p-2 text-neutral-500 hover:text-lime-400 transition-all"><Info size={16} /></button>
                      </div>
                    ))}
                  </div>
                </div>
             ))}
           </div>
         )}
         {profileSubTab === 'tools' && (
           <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
             <PlateCalculator />
             <OneRepMaxCalculator />
             {renderSupplementCoach()}
           </div>
         )}
       </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black font-sans text-gray-100 select-none selection:bg-lime-500 selection:text-black relative">
      <RestTimerOverlay isResting={isResting} restTimer={restTimer} setRestTimer={setRestTimer} setIsResting={setIsResting} />
      <ExerciseInfoModal 
        showInfoModal={showInfoModal} 
        setShowInfoModal={setShowInfoModal} 
        onUpdateImage={handleExerciseImageUpdate} 
        isUploading={isUploading} 
      />
      <EditProfileModal 
        isOpen={showProfileEdit}
        onClose={() => setShowProfileEdit(false)}
        userPhoto={userPhoto}
        onPhotoUpload={handleProfilePhotoUpload}
        userName={userName}
        setUserName={setUserName}
        userHeight={userHeight}
        setUserHeight={setUserHeight}
        userBirthDate={userBirthDate}
        setUserBirthDate={setUserBirthDate}
        onSave={saveProfileDetails}
      />
      
      <div className="max-w-md mx-auto min-h-screen bg-[#0a0a0a] shadow-2xl relative flex flex-col">
        {activeTab !== 'workout' && (
            <header className="sticky top-0 z-40 bg-[#0a0a0a] border-b border-neutral-800 p-5 pt-6">
                <div className="flex justify-between items-center mb-2">
                    <div>
                        <h1 className="text-3xl font-black text-white italic tracking-tighter uppercase">FitTrack<span className="text-lime-400">PRO</span></h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {deferredPrompt && <button onClick={handleInstallClick} className="h-10 w-10 bg-lime-500 text-black rounded-full flex items-center justify-center animate-pulse"><Download size={20} /></button>}
                        <div onClick={() => setShowProfileEdit(true)} className="h-10 w-10 bg-neutral-800 rounded-lg border border-neutral-700 flex items-center justify-center overflow-hidden">
                            {userPhoto ? <img src={userPhoto} className="w-full h-full object-cover" /> : <span className="text-lime-400 font-bold">{userName.substring(0,2).toUpperCase()}</span>}
                        </div>
                    </div>
                </div>
                {activeTab === 'profile' && (
                  <div className="flex p-1 bg-neutral-900 rounded-lg mt-4">
                    <button onClick={() => setProfileSubTab('stats')} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded-md ${profileSubTab === 'stats' ? 'bg-neutral-800 text-white' : 'text-neutral-500'}`}>Körper</button>
                    <button onClick={() => setProfileSubTab('equipment')} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded-md ${profileSubTab === 'equipment' ? 'bg-neutral-800 text-white' : 'text-neutral-500'}`}>Equip</button>
                    <button onClick={() => setProfileSubTab('library')} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded-md ${profileSubTab === 'library' ? 'bg-neutral-800 text-white' : 'text-neutral-500'}`}>Übungen</button>
                    <button onClick={() => setProfileSubTab('tools')} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest rounded-md ${profileSubTab === 'tools' ? 'bg-neutral-800 text-white' : 'text-neutral-500'}`}>Tools</button>
                  </div>
                )}
            </header>
        )}
        
        <main className="flex-1 overflow-y-auto scrollbar-hide">
             {activeTab === 'dashboard' && renderDashboard()}
             {activeTab === 'plan' && renderPlan()}
             {activeTab === 'workout' && (activeWorkout ? renderWorkoutLogger() : renderDashboard())}
             {activeTab === 'history' && renderHistory()}
             {activeTab === 'profile' && renderProfile()}
        </main>

        <nav className="sticky bottom-0 z-50 bg-[#0a0a0a] border-t border-neutral-800 px-2 py-3 flex justify-between items-center">
          {!activeWorkout && (
            <>
            <button onClick={() => setActiveTab('dashboard')} className={`flex flex-col items-center gap-1 w-16 ${activeTab === 'dashboard' ? 'text-lime-400' : 'text-neutral-600'}`}><TrendingUp size={22} /><span className="text-[9px] font-bold uppercase">Dash</span></button>
            <button onClick={() => setActiveTab('plan')} className={`flex flex-col items-center gap-1 w-16 ${activeTab === 'plan' ? 'text-lime-400' : 'text-neutral-600'}`}><CalendarDays size={22} /><span className="text-[9px] font-bold uppercase">Plan</span></button>
            <button onClick={startWorkout} className="bg-lime-400 text-black h-12 w-12 rounded-xl flex items-center justify-center -mt-6 border-4 border-black"><Plus size={28} /></button>
            <button onClick={() => setActiveTab('history')} className={`flex flex-col items-center gap-1 w-16 ${activeTab === 'history' ? 'text-lime-400' : 'text-neutral-600'}`}><History size={22} /><span className="text-[9px] font-bold uppercase">Logs</span></button>
            <button onClick={() => setActiveTab('profile')} className={`flex flex-col items-center gap-1 w-16 ${activeTab === 'profile' ? 'text-lime-400' : 'text-neutral-600'}`}><User size={22} /><span className="text-[9px] font-bold uppercase">Profil</span></button>
            </>
          )}
        </nav>
      </div>
    </div>
  );
}